// import moment from "moment";
//import config from "../config/config";
// import { utcToLocal } from "./DateHelper";
// import { decryptData, encryptData } from "./EncryptHelper";
import dateFormat from "dateformat";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import React, { useMemo, useEffect, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import {
  Card,
  CardBody,
  Col,
  Container,
  Button,
  Input,
  Row,
} from "reactstrap";
import Select from "react-select";
import {
  AsyncClientSelect,
  AsyncCandidateSelect,
  AsyncSourceSelect,
  AsyncUserSelect,
} from "components/Common/AsyncSearchSelects";
import {
  buildAsyncFilterInitialOption,
  buildUpperFilterFieldColClassName,
  isMetadataFilterValueActive,
} from "helpers/metadataFilterHelpers";
import { renderStatusMetadataPill } from "components/Common/renderStatusMetadataPill";
import MetadataLinkTableCell from "components/Common/MetadataLinkTableCell";
import { resolveStatusBadgePresentationExternal } from "libs/context/statusMetadataBridge";

dayjs.extend(customParseFormat);

const TIME_ONLY_FORMATS = [
  "HH:mm",
  "HH:mm:ss",
  "h:mm A",
  "hh:mm A",
  "h:mm:ss A",
  "hh:mm:ss A",
];

const DATE_ACCESSOR_HINTS = [
  "date",
  "day",
  "dob",
  "created",
  "updated",
  "update",
  "create",
  "register",
  "bill",
  "activation",
  "assign",
  "closed",
  "start",
  "end",
  "from",
  "to",
];

const TIME_ACCESSOR_HINTS = ["time", "hour", "minute", "second"];

const parseDateLikeValue = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (value instanceof Date) {
    const d = dayjs(value);
    return d.isValid() ? d : null;
  }
  if (typeof value === "number") {
    const timestamp = value > 1e11 ? value : value > 1e9 ? value * 1000 : null;
    if (timestamp == null) return null;
    const d = dayjs(timestamp);
    return d.isValid() ? d : null;
  }

  const raw = String(value).trim();
  if (!raw) return null;

  const strictDate = dayjs(
    raw,
    [
      "YYYY-MM-DD",
      "YYYY/MM/DD",
      "MM-DD-YYYY",
      "MM/DD/YYYY",
      "DD-MM-YYYY",
      "DD/MM/YYYY",
      "MMM D, YYYY",
      "MMMM D, YYYY",
      "YYYY-MM-DD HH:mm",
      "YYYY-MM-DD HH:mm:ss",
      "MM-DD-YYYY HH:mm",
      "MM-DD-YYYY HH:mm:ss",
    ],
    true
  );
  if (strictDate.isValid()) return strictDate;

  const looseDate = dayjs(raw);
  return looseDate.isValid() ? looseDate : null;
};

const parseTimeOnlyValue = (value) => {
  if (value === null || value === undefined) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  const parsed = dayjs(raw, TIME_ONLY_FORMATS, true);
  return parsed.isValid() ? parsed : null;
};

const hasDatePattern = (value = "") =>
  /\d{4}[-/]\d{1,2}[-/]\d{1,2}/.test(value) ||
  /\d{1,2}[-/]\d{1,2}[-/]\d{2,4}/.test(value) ||
  /^[A-Za-z]{3,9}\s+\d{1,2},?\s+\d{4}/.test(value);

const hasTimePattern = (value = "") =>
  /\b([01]?\d|2[0-3]):[0-5]\d(:[0-5]\d)?\b/.test(value) ||
  /\b(1[0-2]|0?[1-9]):[0-5]\d(:[0-5]\d)?\s?(AM|PM)\b/i.test(value) ||
  /T\d{1,2}:\d{2}(:\d{2})?/.test(value);

/** Fetch file bytes and trigger a direct download (no new tab). */
// export const downloadFileFromUrl = async (fileUrl, options = {}) => {
//   const { openInNewTabOnFailure = true } = options;
//   const url = String(fileUrl ?? "").trim();
//   if (!url) {
//     toast.error("Download link is missing.");
//     return false;
//   }
//   try {
//     const response = await fetch(url);
//     if (!response.ok) throw new Error("Download failed");
//     const blob = await response.blob();
//     const blobUrl = window.URL.createObjectURL(blob);
//     const link = document.createElement("a");
//     link.href = blobUrl;
//     const fromPath = url.split("/").pop()?.split("?")[0];
//     link.download = fromPath || "download";
//     document.body.appendChild(link);
//     link.click();
//     document.body.removeChild(link);
//     window.URL.revokeObjectURL(blobUrl);
//     return true;
//   } catch {
//     if (openInNewTabOnFailure) {
//       window.open(url, "_blank", "noopener,noreferrer");
//       return true;
//     }
//     toast.error("Incorrect URL ! Unable to download file.");
//     return false;
//   }
// };

export const resolveFilenameFromContentDisposition = (header = "") => {
  const raw = String(header ?? "").trim();
  if (!raw) return "";

  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(raw);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].trim());
    } catch {
      return utf8Match[1].trim();
    }
  }

  const quotedMatch = /filename="([^"]+)"/i.exec(raw);
  if (quotedMatch?.[1]) return quotedMatch[1].trim();

  const plainMatch = /filename=([^;]+)/i.exec(raw);
  if (plainMatch?.[1]) return plainMatch[1].trim().replace(/"/g, "");

  return "";
};

/** Save a Blob/ArrayBuffer response as a browser file download. */
export const downloadBlobAsFile = (blob, filename = "download") => {
  if (!blob) {
    toast.error("Download file is missing.");
    return false;
  }

  try {
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = String(filename || "download").trim() || "download";
    link.rel = "noopener noreferrer";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
    return true;
  } catch {
    toast.error("Unable to download file.");
    return false;
  }
};

/** Trigger direct browser download from a file URL (without fetch). */
export const downloadFileFromUrl = (fileUrl) => {
  const url = String(fileUrl ?? "").trim();

  if (!url) {
    toast.error("Download link is missing.");
    return false;
  }

  try {
    const link = document.createElement("a");
    link.href = url;

    // Optional: extract filename
    const fromPath = url.split("/").pop()?.split("?")[0];
    link.download = fromPath || "download";

    link.rel = "noopener noreferrer";
    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);

    return true;
  } catch {
    toast.error("Incorrect URL ! Unable to download file.");
    return false;
  }
};

export const convertToMMDDYYYY = (value) => {
  const parsed = parseDateLikeValue(value);
  if (!parsed) return value ?? "";
  return parsed.format("MM-DD-YYYY");
};

export const convertTo24HourTime = (value) => {
  const timeOnly = parseTimeOnlyValue(value);
  if (timeOnly) return timeOnly.format("hh:mm A");

  const parsed = parseDateLikeValue(value);
  if (!parsed) return value ?? "";
  return parsed.format("hh:mm A");
};

export const convertToDateAndTime = (value) => {
  const parsed = parseDateLikeValue(value);
  if (!parsed) return value ?? "";
  return parsed.format("MM-DD-YYYY hh:mm A");
};

export const convertToDateAndTimeWithSeconds = (value) => {
  const parsed = parseDateLikeValue(value);
  if (!parsed) return value ?? "";
  return parsed.format("MM-DD-YYYY hh:mm:ss A");
};

/** Metadata `table.columns[].type` value for date+time cells. */
export const isMetadataDateTimeTableColumnType = (columnType = "") =>
  String(columnType ?? "").trim().toLowerCase() === "datetime";

/** Metadata `table.columns[].type` value for URL cells (copy / shorten in modal). */
export const isMetadataLinkTableColumnType = (columnType = "") =>
  String(columnType ?? "").trim().toLowerCase() === "link";

export const extractShortenedLinkFromApiResponse = (response = {}) => {
  const payload = response?.data ?? response ?? {};
  if (typeof payload === "string") return payload.trim();
  return String(
    payload?.shortUrl ??
      payload?.shortLink ??
      payload?.url ??
      payload?.link ??
      ""
  ).trim();
};

export const copyTextToClipboard = async (
  text,
  errorMessage = "Unable to copy to clipboard"
) => {
  const value = String(text ?? "").trim();
  if (!value) {
    toast.error(errorMessage);
    return false;
  }
  try {
    await navigator.clipboard.writeText(value);
    toast.success(`Copied: ${value}`, {
      style: {
        whiteSpace: "normal",
        wordBreak: "break-word",
        overflowWrap: "anywhere",
        maxWidth: "min(28rem, 92vw)",
      },
      bodyStyle: {
        whiteSpace: "normal",
        wordBreak: "break-word",
        overflowWrap: "anywhere",
      },
    });
    return true;
  } catch {
    toast.error(errorMessage);
    return false;
  }
};

/** True when an API value carries both date and time (e.g. ISO `2026-07-09T02:30:06.977`). */
export const isApiDateTimeLikeValue = (value) => {
  if (value === null || value === undefined || value === "") return false;
  if (value instanceof Date) return true;
  if (typeof value === "number") return !Number.isNaN(value);
  const raw = String(value).trim();
  if (!raw) return false;
  if (/T\d{1,2}:\d{2}/.test(raw)) return true;
  return hasDatePattern(raw) && hasTimePattern(raw);
};

/** Format API datetime for metadata-driven tables — preserves time from the payload. */
export const formatMetadataTableDateTimeForDisplay = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const parsed = parseDateLikeValue(value);
  if (!parsed) return String(value).trim();
  const raw = String(value).trim();
  const includesSeconds =
    /T\d{2}:\d{2}:\d{2}/.test(raw) || /\d{1,2}:\d{2}:\d{2}/.test(raw);
  return includesSeconds
    ? parsed.format("MM-DD-YYYY hh:mm:ss A")
    : parsed.format("MM-DD-YYYY hh:mm A");
};

/** Metadata table cell formatter — honors column type and API datetime values. */
export const formatMetadataTableCellDateTime = (value, columnType = "", accessor = "") => {
  if (isMetadataDateTimeTableColumnType(columnType) || isApiDateTimeLikeValue(value)) {
    return formatMetadataTableDateTimeForDisplay(value);
  }
  return formatDateTimeValueForTable(value, accessor);
};

export const formatDateTimeValueForTable = (value, accessor = "") => {
  if (value === null || value === undefined || value === "") return value ?? "";
  if (typeof value === "boolean") return value;
  if (typeof value === "object" && !(value instanceof Date)) return value;

  const raw = String(value).trim();
  if (!raw) return value;

  const normalizedAccessor = String(accessor || "")
    .toLowerCase()
    .replace(/[_\s-]/g, "");

  const hasDateHint = DATE_ACCESSOR_HINTS.some((hint) => normalizedAccessor.includes(hint));
  const hasTimeHint = TIME_ACCESSOR_HINTS.some((hint) => normalizedAccessor.includes(hint));
  const looksDate = hasDatePattern(raw);
  const looksTime = hasTimePattern(raw);

  if (isApiDateTimeLikeValue(raw)) {
    return formatMetadataTableDateTimeForDisplay(value);
  }

  if ((hasDateHint && hasTimeHint) || (looksDate && looksTime)) {
    return convertToDateAndTime(value);
  }
  if (hasTimeHint || (!hasDateHint && looksTime && !looksDate)) {
    return convertTo24HourTime(value);
  }
  if (hasDateHint || looksDate || value instanceof Date || typeof value === "number") {
    return convertToMMDDYYYY(value);
  }

  return value;
};


export const numberToOrdinal = (number) => {
  const remainder10 = number % 10;
  const remainder100 = number % 100;

  if (remainder10 === 1 && remainder100 !== 11) {
    return `${number}st`;
  } else if (remainder10 === 2 && remainder100 !== 12) {
    return `${number}nd`;
  } else if (remainder10 === 3 && remainder100 !== 13) {
    return `${number}rd`;
  } else {
    return `${number}th`;
  }
};

export const allowOnlyNumber = (value = "") => {
  const numberString = String(value).replace(/[^0-9.]/g, "");
  return numberString.replace(/(\..*)\./g, "$1");
};

/**
 * US phone display for tables/lists — e.g. `(456)778-9332`.
 * Strips non-digits; uses last 10 digits when longer (leading `1` country code).
 */
export const US_PHONE_INPUT_FORMAT = "(###)###-####";

/** Strip phone formatting — supports paste e.g. `(267)366-3636` or `+12567890678`. Max 10 US digits. */
export const toPhoneDigits = (value = "") => {
  let digits = String(value ?? "").replace(/\D/g, "");
  // Drop US country code (+1) so pasted E.164 values become 10 local digits
  if (digits.length === 11 && digits.startsWith("1")) {
    digits = digits.slice(1);
  }
  return digits.slice(0, 10);
};

export const phoneNumberShowFormat = (value = "") => {
  if (value === null || value === undefined) return "";
  const raw = String(value).trim();
  if (!raw) return "";

  const digits = raw.replace(/\D/g, "");
  if (!digits) return raw;

  let ten = digits;
  if (digits.length === 11 && digits.startsWith("1")) {
    ten = digits.slice(1);
  } else if (digits.length > 10) {
    ten = digits.slice(-10);
  }

  if (ten.length === 10) {
    return `(${ten.slice(0, 3)})${ten.slice(3, 6)}-${ten.slice(6)}`;
  }

  return raw;
};

export const isNumberOnly = (value = "") => {
  return /^[0-9]*$/.test(String(value));
};

export const getInitials = (name) => {
  const initials = name
    .split(" ")
    .map((word) => word.charAt(0))
    .join("")
    .toUpperCase();

  return initials;
};

export const profilePic = (
  firstName,
  lastName,
  pic,
  radius = "30px",
  fontSize = "10px",
  display = "block"
) => {
  if (display !== "block") {
    return "";
  } else if (pic) {
    return <img src={`${process.env.REACT_APP_ASSETS_URL}/${pic}`} alt="img" />;
  } else {
    let name = firstName + " " + lastName;

    return (
      <div className="user-avatar" style={{ height: radius, width: radius, fontSize: fontSize }}>
        {getInitials(name)}
      </div>
    );
  }
};

export const checkPermission = (route, action = 'view') => {
  const user_data = localStorage.getItem("user_data") ? localStorage.getItem("user_data") : {};//decryptData(localStorage.getItem("user_data")) : {};

  return user_data.urole_permission?.[route]?.[action] ?? false;
};

export const formatDataBeforeSend = (data, inputFields, formatOptions) => {
  const filterData = {};

  Object.entries(inputFields).forEach(([key, value], index) => {
    filterData[key] = value ? value : data[key] ? data[key] : ''; // Assign the value to `filterData`
    //console.log(`Index: ${index}, Key: ${key}, Value: ${value}`);
  });

  if (formatOptions) {
    formatOptions.forEach(function (item) {
      if (item.type == 'react-select') {
        filterData[item.column] = filterData[item.column]?.value ?? '';
      } else if (item.type == 'react-select-multiple') {
        let temp = [];
        filterData[item.column]?.forEach(function (val) {
          temp.push(val.value);
        });
        filterData[item.column] = temp.join(',');
      } else if (item.type == 'react-datepicker') {
        filterData[item.column] = filterData[item.column] ? dateFormat(filterData[item.column], "dd-mm-yyyy") : '';
      } else if (item.type == 'react-timepicker') {
        if (!filterData[item.column] || filterData[item.column] == '00:00:00') {
          filterData[item.column] = '';
        } else {

          // This works for react-time-picker
          const now = new Date(Date.parse('2024-01-01 ' + filterData[item.column]));
          const hours = String(now.getHours()).padStart(2, '0');
          const minutes = String(now.getMinutes()).padStart(2, '0');
          const seconds = String(now.getSeconds()).padStart(2, '0');

          filterData[item.column] = `${hours}:${minutes}:${seconds}`;
        }
      } else if (item.type == 'file' && filterData[item.column] == null) {
        filterData[item.column] = '';
      }
      // else if(item.type='hidden'){
      //   filterData[item.column] = filterData[item.labelColumn] ? filterData[item.labelColumn] : filterData[item.column];
      // }
    });
  }
  //console.log('filterdata2 =>',filterData);
  let formData = new FormData();
  for (let key in filterData) {
    formData.append(key, (key?.endsWith('_is_active') ? (filterData[key] ? 1 : 0) : filterData[key]));
  }

  return formData;
}

export const formatDataBeforeDisplay = (data, formatOptions) => {
  const formData = JSON.parse(JSON.stringify(data));

  Object.keys(formData).forEach(function (item) {
    formData[item] = data[item] ?? '';
  });
  //console.log('form =>',formData);
  if (formatOptions) {
    formatOptions.forEach(function (item) {
      if (item.type == 'react-select') {
        formData[item.column] = { value: formData[item.column], label: formData[item.labelColumn] };
      } else if (item.type == 'react-datepicker') {
        if (!formData[item.column] || formData[item.column] == '0000-00-00') {
          formData[item.column] = null;
        } else {
          formData[item.column] = new Date(Date.parse(formData[item.column]));
        }
      } else if (item.type == 'react-timepicker') {
        if (!formData[item.column] || formData[item.column] == '00:00:00') {
          formData[item.column] = null;
        } else {

          // This is for react-time-picker
          formData[item.column] = new Date('2024-04-24 ' + formData[item.column]);
        }
      }
    });
  }

  return formData;
}

// export const formatDataBeforeView = (data, formatOptions) => {
//   const formData = JSON.parse(JSON.stringify(data));

//   Object.keys(formData).forEach(function(item) {
//     formData[item] = data[item] ?? '';
//   });

//   if (formatOptions) {
//     formatOptions.forEach(function(item) {
//       if (item.type == 'date') {
//         if (! formData[item.column] || formData[item.column] == '0000-00-00') {
//           formData[item.column] = '';
//         } else {
//           formData[item.column] = dateFormat(Date.parse(formData[item.column]), 'dd/mm/yyyy');
//         }
//       } else if (item.type == 'time') {
//         if (! formData[item.column] || formData[item.column] == '00:00:00') {
//           formData[item.column] = '';
//         } else {
//           formData[item.column] = formatAMPM(new Date(Date.parse('2024-04-30 ' + formData[item.column])));
//         }
//       } else if (item.type == 'datetime') {
//         if (! formData[item.column] || formData[item.column] == '0000-00-00 00:00:00') {
//           formData[item.column] = '';
//         } else {
//           formData[item.column] = utcToLocal(formData[item.column]);
//         }
//       } else if (item.type == 'yesNo') {
//         formData[item.column] = formData[item.column] == 1 ? 'Yes' : 'No';
//       } else if (item.type == 'capitalize') {
//         formData[item.column] = capitalize(formData[item.column]);
//       }
//     });
//   }

//   return formData;
// }

export const cacheToStorage = (key, data) => {
  // if (! config.isCache) {
  //   return;
  // }

  let currentTime = dateFormat('', "dd mmm yyyy HH:MM:ss");

  const dataToStore = {
    [key]: data,
    time: currentTime
  };

  localStorage.setItem(key, dataToStore); //encryptData(dataToStore)
}
export const allDescriptionKeys = ['description', 'comment', 'htmlContent', 'service_description', 'step_description', 'price_description', 'member_comment', 'blog_content']

export const validorSettings = () => {
  return {
    messages: {
      max: 'The :attribute must be at most :max digits',
      min: 'The :attribute must be at least :min digits',

      // email: 'Please enter an valid email',
      // url: 'Please enter an valid URL',
      // default: 'Validation has failed!'
    },
    autoForceUpdate: this
  }
}

export const formatAMPM = (date) => {
  let hours = date.getHours();
  let minutes = date.getMinutes();
  const ampm = hours >= 12 ? 'pm' : 'am';
  hours = hours % 12;
  hours = hours || 12; // the hour '0' should be '12'
  minutes = minutes < 10 ? '0' + minutes : minutes;

  return hours + ':' + minutes + ' ' + ampm;
}

export const capitalize = (value) => {
  if (!value)
    return '';

  return value.charAt(0).toUpperCase() + value.slice(1);
}

export const decodeHtmlEntities = (encodedString) => {
  const elem = document?.createElement('textarea');
  elem.innerHTML = encodedString;
  return elem.value;
}

export const decodeHtmlEntities1 = (encodedString) => {
  const elem = document?.createElement('textarea');
  elem.innerHTML = encodedString;
  return elem.value;
}

export const paymentModeLabels = (payment_mode) => {

  if (payment_mode == 'bank-transfer') {
    return 'Bank Transfer';
  }
  else if (payment_mode == 'cash') {
    return 'Cash';
  }
  else if (payment_mode == 'cheque') {
    return 'Cheque';
  }
  else {
    return '';
  }
}

export const checkBadgeColour = (statusType) => {
  switch (statusType) {
    case "1":
      return `badge-soft-success`
    case "0":
      return `badge-soft-warning`;
    default:
      return `badge-soft-success`
  }
}

export const checkBadgeStatus = (status) => {
  switch (status) {
    case "1":
      return `Active`
    case "0":
      return `InActive`;
    default:
      return `Active`
  }
}

export const checkBadgeStatusForAdvantage = (status) => {
  switch (status) {
    case "1":
      return `Show`
    case "0":
      return `Hide`;
    default:
      return `Show`
  }
}

export const testimonialShowOnHome = (status) => {
  switch (status) {
    case "1":
      return `Show`
    case "0":
      return `Hide`;
    default:
      return `Show`
  }
}

export const urlEndPoint = {
  TestimonialListUrl: '/testimonials-list',
  TestimonialAddUrl: '/add-testimonials',
  TestimonialEditUrl: '/edit-testimonials',
  TeamListUrl: '/teams-list',
  TeamAddUrl: '/add-team',
  TeamEditUrl: '/edit-team',
  ContactListUrl: '/contacts-list',
  ContactViewUrl: '/contacts-view',
  PartnerListUrl: '/partners-list',
  PartnerAddUrl: '/add-partner',
  PartnerEditUrl: '/edit-partner',
  AdvantageListUrl: '/advantage-list',
  AdvantageAddUrl: '/add-advantage',
  AdvantageEditUrl: '/edit-advantage',
  RecuitmentEditUrl: '/recuitment-update',
  ContactEditUrl: '/edit-contact',
  AboutUsListUrl: '/aboutus-list',
  AboutUsAddUrl: '/add-aboutus',
  MissionVissionUrl: '/about-mission-vision',
  MainPriorities: '/about-main-priorities',
  ValuableTeam: '/about-valuable-team',
  RecruitmentProcessAddUrl: '/about-recruit-process-add',
  RecruitmentProcessEditUrl: '/about-recruit-process-edit',
  RecruitmentProcessListUrl: '/about-recruit-process-list',
  AboutUsProcessUrl: '/about-us-process',
  AboutUsPartnerUrl: '/about-us-partner',
  RecruitingProcessListUrl: '/recruiting-process-list',
  RecruitingProcessFormUrl: '/recruiting-process-form',
  RecruitingProcessEditUrl: '/recruiting-process-edit',
  RecruitingProcessAddUrl: '/recruiting-process-add',

  BlogDetailsFormUrl: '/blog-details-form',
  getCategoryFormUrl: '/category-list',
  getBlogListUrl: '/blogs-list',
  blogAddUrl: '/add-blog',
  blogEditUrl: '/edit-blog',
  homePageUrl: '/home-page',
  clientDetaisUrl: '/client-details',
  callDetailsUrl: '/call-details',
  notificationCallDetailsUrl: '/call/details',
  callDetailsNewUrl: '/call-details-new',
  jobCategoryUrl: '/job-category',
  aiFadvPackageUrl: '/ai-fadv-package',
  clientPlan: '/client-plan',
  jobRequiredUrl: '/job-required',
  taskGroupSettingsUrl: '/task-group-settings',
  aiScheduleMatrixGroupUrl: '/ai-schedule-matrix-group',
  questionTypeUrl: '/question-type',
  auditingParameter: '/auditing-parameter',
  registerFieldUrl: '/register-field',
  jobUrl: '/jobs',
  sourceUrl: '/source',
  reasonUrl: '/reasons',
  badFitResolutionReasonUrl: '/bad-fit-resolution-reason',
  docRequiredUrl: '/doc-required',
  onboardingDocumentsUrl: '/onboarding-documents',
  taskBucketUrl: '/task-bucket',
  newCandidateAdd: 'new-candidate',
  allCandidates: 'candidates',
  candidateHireStatus: 'candidate-hire-status',
  externalLeads: 'external-leads',
  candidateOnboarding: 'tsa-check',
  uploadDrugCSV: 'upload-drug-csv',
  uploadBulkCV: 'upload-bulk-cv',
  uploadProfileCSV: 'upload-profile-csv',
  candidateCallBackRequest: 'candidate-call-back-request',
  fadvCsvLog: 'fadv-csv-log',
  fadvOrderLog: 'fadv-order-log',
  fadvProfileOrderLog: 'fadv-profile-order-log',
  verifyFadvCsv: 'verify-fadv-csv',
  blockFadvRequestor: 'block-fadv-requestor',
  onboardingDocUrl: '/onboarding-documents',
  verifyApplicationFile: 'verify-application-file',

  // reports section
  inprogressReport: 'inprogress-report',
  clientReport: 'client-report',
  eligibleReport: 'eligible-report',
  closureReport: 'closure-report',
  clientUserReport: 'client-user-report',
  clientSummaryReport: 'candidate-summary-report',
  leadsAddedReport: 'leads-added-report',
  clientStatusReport: 'client-status-report',
  teamPerformanceReport: 'team-performance-report',
  backendPerformanceReport: 'backend-performance-report',
  monthWiseReport: 'month-report',
  compliancyReport: "compliancy-report",
  notInterestedAnymore: "not-interested-anymore",
  segmentCompletionReport: "segment-completion-report",
  callerPerformanceReport: "caller-performance-report",
  readyToSendReport: "ready-to-send-report",
  drugCsvUploadReport: "drug-csv-upload-report",
  sessionActivityReport: "session-activity-report",
  userClientActivityReport: "user-client-activity-report",
  rtrInternalReport: "rtr-internal-report",
  monthWiseSummaryReport: 'month-wise-summary-report',
  smePerformanceReport: 'sme-performance-report',
  inProgressReport: 'inprogress-report',

  // compnay section

  company: "company",
  findCompanyUser: "find-company-user",
  clientUser: "client",
  source: "client/source",
  clientReview: "client/review",
  clientReviewEdit: "client-review-edit",
  notificationClientReviewEdit: "Home/Index",
  clientReviewAdd: "client-review-add",
  preQuestioning: "pre-questioning",
  jobDescription: "job-description",
  jobDescriptionAddEdit: "job-description-upsert",
  clientAppreciation: "client-appreciation",
  clientAppreciations: "client-appreciations",
  onboardingList: "onboarding-list",
  addEditOnboarding: "add-edit-onboarding",
  offBoardingList: "offboarding-list",
  clientRegister: "client-register",
  clientRegisterAddEdit: "client-register-add-edit",
  ClientJobDescription: "client-job-description",

  // settings
  mailTemplate: "mail-template",
addEditMail: "add-edit-mail-template",
notificationSetting: "notification-setting",
AISmsSetting: "ai-sms-setting",
addEditAISms: "add-edit-ai-sms-template",
broadcastNotification: "broadcast-notification",
userIpSettings: "user-ip-settings",
testEmailServer: "test-email-server",
conversationTemplate: "conversation-template",
addEditConversation: "add-edit-conversation-template",
conversationMatrix: "conversation-matrix",

AIAddonCallTemplateSetting: "ai-addon-call-template-setting",
addEditAIAddonCallTemplate: "add-edit-aiaddon-call-template-template",

// incentive
achievements: "achievements",
monthlyList: "monthly-list",
monthlyDetails: "monthly-details",
achievementsTwo: "achievements-two",
monthlyListTwo: "monthly-list-two",
monthlyDetailsTwo: "monthly-details-two",

achievementsThree: "achievements-three",
monthlyListThree: "monthly-list-three",
monthlyDetailsThree: "monthly-details-three",

// team Recognition
teamRecognitionAchievements: "team-recognition-achievements",
teamRecognitionMonthlyList: "team-recognition-monthly-list",
teamRecognitionMonthlyDetails: "team-recognition-monthly-details",

// audit
callAudit: "call-audit",
editcallAudit: "edit-call-audit",
viewcallAudit: "view-call-audit",
createAudit: "create-audit",


// user and team

team: "team",
workShift: "work-shift",
workLetter: "warning-letter",
userType: "user-type",
userTypeAddEdit: "user-type-add-edit",
manageUser: "manage-user",
manageUserAddEdit: "manage-user-add-edit",
companyUser: "company-user",
companyUserAddEdit: "company-user-add-edit",
attendanceReport: "attendance-report",
userActivities: "user-activities",
notInterestedReport: "not-interested-report",
notificationManageUser: "User/List",


// call
schedule: "schedule",
aiScheduleMatrix: "ai-schedule-matrix",
interview: "interview",
today: "today",
recruiterToday: "today/vm",
wrongNumber: "wrong-number",
underAge: "under-age",
/** Call Executive — order pipeline (distinct from Manager `inProgress`). */
executiveOrderInProgress: "order-in-progress",
inProgress: "in-progress",
readyToSendUpdate: "ready-to-send-update",
furtherReview: "further-review",
fedexDriverReview: "fedex-review",
myCall: "my-call",
deactivateCandidate: "deactivate-candidate",

// candidate
verifyTest: "verify-test",
modifyClosure: "modify-closure",
badFit: "bad-fit",

// AI recruiter
aiRecruiterLog: "ai-recruiter-log",
callScheduledReport: "call-scheduled-report",

allNotifications: "all-notifications",
taskPlanner: "task-planner",
clientJourney: "client-journey",
internalTaskPlanner: "internal-task-planner",
accessDenied: "access-denied",
backendDashboard: "backend-dashboard",
callDetails: "call-details",
homeHealthStatus: "home-health",
auditingTemplates: "auditing-templates",
legends: "legends",
twilioPhone : "twilio-phone",
accountExecutiveDashboard: "account-executive-dashboard",
recruiterDashboard: "recruiter-dashboard",
simpleAllNotifications: "simple-all-notifications",

rtrAcknowledgement: "rtr-acknowledgement",
AIDashboard: "ai-dashboard",
AIFadvReport: "ai-fadv-report",

notOnAI: "not-on-ai",




  socialMediaUrl: '/social-url',
  serviceUrl: '/service-list',
  serviceAddUrl: '/service-add',
  serviceEditUrl: '/service-edit',

  serviceRecruitUrl: '/service-recruit-list',
  serviceRecruitAddUrl: '/service-recruit-add',
  serviceRecruitEditUrl: '/service-recruit-edit',

  serviceProcessUrl: '/service-process-list',
  serviceProcessAddUrl: '/service-process-add',
  serviceProcessEditUrl: '/service-process-edit',
  newsLetterSubscriptionList: '/newsletter-subscription-list',
  privacyPolicyDetails: '/privacy-policy',
  checkoutResponse: '/checkout-response',
  servicePartnerListUrl: '/service-partners-price-list',
  servicePartnerAddUrl: '/add-service-partners-price',
  servicePartnerEditUrl: '/edit-service-partners-price',
  transactionURL: '/transaction-list',
  PartnerContactsListUrl: '/partners-contacts-list',
  PartnerContactViewUrl: '/partner-contacts-view',

  AIRecruiterClient: '/ai-recruiter-client'

}

export const truncateText = (text, maxLength = 30) => {
  return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
}

/** e.g. user@gmail.com, user@ancile.services */
export const EMAIL_REGEX = /^[\w-.]+@([\w-]+\.)+[\w-]{2,}$/i;

export const isValidEmail = (value) =>
  EMAIL_REGEX.test(String(value ?? "").trim());

export const areValidEmails = (value) =>
  String(value ?? "")
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean)
    .every(isValidEmail);

export const resolveFadvIsOldForSubmit = ({ isOtpBased = false, isOld = false } = {}) =>
  Boolean(isOtpBased && isOld);

/** Validation for AI First Advantage Login modal — all credential fields required. */
export const validateAiFadvLoginForm = (form = {}, itemId = "") => {
  if (!String(itemId ?? "").trim()) {
    return { account: "Unable to identify the selected row" };
  }

  const errors = {};
  if (!String(form?.account ?? "").trim()) {
    errors.account = "Account number is required";
  }
  if (!String(form?.user ?? "").trim()) {
    errors.user = "Login ID is required";
  }
  if (!String(form?.password ?? "").trim()) {
    errors.password = "Password is required";
  }
  if (!String(form?.secret ?? "").trim()) {
    errors.secret = "Secret is required";
  }

  const userEmail = String(form?.userEmail ?? "").trim();
  if (!userEmail) errors.userEmail = "User email is required";
  else if (!isValidEmail(userEmail)) errors.userEmail = "Enter a valid email";

  const outlookEmail = String(form?.outlookEmail ?? "").trim();
  if (!outlookEmail) errors.outlookEmail = "Outlook email is required";
  else if (!isValidEmail(outlookEmail)) errors.outlookEmail = "Enter a valid email";

  return errors;
};

export const validateImageDimensionsForHomeBanner = (file, expectedWidth, expectedHeight) => {
  return new Promise((resolve) => {
    if (!file || !file.type.startsWith('image/')) {
      toast?.error("Invalid file type. Please upload an image.");
      return resolve(false);
    }

    const img = new Image();
    const objectUrl = URL?.createObjectURL(file);

    img.onload = () => {
      URL?.revokeObjectURL(objectUrl);
      const isValid = img.width === expectedWidth && img.height === expectedHeight;
      if (!isValid) {
        toast?.error(`Image dimensions must be ${expectedWidth} x ${expectedHeight}px.`);
      }
      resolve(isValid);
    };

    img.onerror = () => {
      URL?.revokeObjectURL(objectUrl);
      toast?.error("Failed to load image for dimension check.");
      resolve(false);
    };

    img.src = objectUrl;
  });
};

export const checkImageAspectRatio = (file, expectedRatio = 1 / 1, tolerance = 0.01) => {
  return new Promise((resolve) => {
    const img = new Image();

    img.onload = () => {
      const actualRatio = img.width / img.height;
      const isValid = Math.abs(actualRatio - expectedRatio) < tolerance;
      if (!isValid) {
        // toast?.error(`Image dimensions must be 1:1 aspect ratio`);
      }
      resolve(isValid);
    };

    img.onerror = () => {
      // reject(new Error("Failed to load image."));
      toast?.error("Failed to load image for dimension check.");
      resolve(false);
    };

    img.src = URL.createObjectURL(file);
  });
};

export const validateImageDimensionsForBlogBanner = (file, expectedWidth, expectedHeight) => {
  return new Promise((resolve) => {
    if (!file || !file.type.startsWith('image/')) {
      toast?.error("Invalid file type. Please upload an image.");
      return resolve(false);
    }

    const img = new Image();
    const objectUrl = URL?.createObjectURL(file);

    img.onload = () => {
      URL?.revokeObjectURL(objectUrl);
      const isValid = img.width === expectedWidth && img.height === expectedHeight;
      if (!isValid) {
        // toast?.error(`Image dimensions must be ${expectedWidth} x ${expectedHeight}px.`);
      }
      resolve(isValid);
    };

    img.onerror = () => {
      URL?.revokeObjectURL(objectUrl);
      toast?.error("Failed to load image for dimension check.");
      resolve(false);
    };

    img.src = objectUrl;
  });
};

export const truncateTextForTable = (text, maxLength = 100) => {
  return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
}

// export const validateImageDimensionsForAllowHeightWidth = (file, expectedWidth, expectedHeight) => {
//   return new Promise((resolve) => {
//     if (!file || !file.type.startsWith('image/')) {
//       toast?.error("Invalid file type. Please upload an image.");
//       return resolve(false);
//     }

//     const img = new Image();
//     const objectUrl = URL?.createObjectURL(file);

//     img.onload = () => {
//       URL?.revokeObjectURL(objectUrl);
//       const isValid = ((img.width === expectedWidth || (img.width > expectedWidth && img.width < expectedWidth+10)) && ((img.height === expectedHeight) || (img.height > expectedWidth && img.height < expectedWidth+10)));
//       if (!isValid) {
//         toast?.error(`Image dimensions must be ${expectedWidth} x ${expectedHeight}px.`);
//       }
//       resolve(isValid);
//     };

//     img.onerror = () => {
//       URL?.revokeObjectURL(objectUrl);
//       toast?.error("Failed to load image for dimension check.");
//       resolve(false);
//     };

//     img.src = objectUrl;
//   });
// };



export const checkBadgeColourPayment = (statusType) => {
  switch (statusType) {
    case "1":
      return `badge-soft-success`
    case "0":
      return `badge-soft-warning`;
    case "2":
      return `badge-soft-danger`;
    default:
      return `badge-soft-success`
  }
}

export const checkBadgeStatusPayment = (status) => {
  switch (status) {
    case "1":
      return `Paid`
    case "0":
      return `Pending`;
      case "2":
      return `Failed`;
    default:
      return `Active`
  }
}

// src/utils/filterMenu.js
export const filterMenu = (menu) => {
  return menu
    .filter(group => group.permission === true)
    .map(group => ({
      ...group,
      items: group.items
        ?.filter(item => item.permission === true)
        .map(item => ({
          ...item,
          children: item.children
            ? item.children.filter(child => child.permission === true)
            : undefined
        }))
        .filter(item => item.routeLink || (item.children && item.children.length))
    }))
    .filter(group => group.items && group.items.length > 0);
};


export const  staticPermissionData = () =>{
  return {
    "HomeAccess": true,
    "MasterAccess": true,
    "MasterReasonsAccess": true,
    "MasterTaskBucketAccess": true,
    "MasterLegends": true,
    "LegendsAddAccess": true,
    "LegendsUpdateAccess": true,
    "MasterAuditingParameters": true,
    "AuditingParametersAddAccess": true,
    "AuditingParametersUpdateAccess": true,
    "AuditingTemplateAddAccess": true,
    "UserTypeAccess": true,
    "TeamAccess": true,
    "TeamAddAccess": true,
    "TeamUpdateAccess": true,
    "UserAccess": true,
    "UserAddAccess": true,
    "UserEditAccess": true,
    "UserResetBlockAccess": true,
    "UserClientAccess": true,
    "UserClientBypassRecognitionAccess": true,
    "UserActivityListAccess": true,
    "EmailAccess": true,
    "PhoneAccess": true,
    "UserPermissionAccess": true,
    "UserAskFunnlAssistantAccess": true,
    "UserWorkShiftAccess": true,
    "UserWarningLetterAccess": true,
    "UserAttendanceAccess": true,
    "UserAttendanceResetAccess": true,
    "DocumentRequiredAccess": true,
    "JobCategoryAccess": true,
    "JobCategoryAddAccess": true,
    "JobCategoryUpdateAccess": true,
    "RegisterFieldAccess": true,
    "RegisterFieldSubmitAccess": true,
    "JobRequireAccess": true,
    "JobRequireAddAccess": true,
    "JobRequireUpdateAccess": true,
    "SourceAccess": true,
    "SourceAddAccess": true,
    "SourceUpdateAccess": true,
    "QuestionTypeAccess": true,
    "QuestionTypeAddAccess": true,
    "QuestionTypeUpdateAccess": true,
    "JobAccess": true,
    "JobAddAccess": true,
    "JobUpdateAccess": true,
    "BadFitResolutionReasonAccess": true,
    "BadFitResolutionReasonAddAccess": true,
    "BadFitResolutionReasonUpdateAccess": true,
    "PaymentReportAccess": true,
    "PushToAirTableAccess": true,
    "ClientMenuAccess": true,
    "ShareFormAccess": true,
    "ClientCompanyAccess": true,
    "ClientCompanyAddAccess": true,
    "ClientCompanyUpdateAccess": true,
    "ClientCompanyUserAccess": true,
    "ClientCompanyUserAddAccess": true,
    "ClientCompanyUserUpdateAccess": true,
    "ClientCompanyUserViewAccess": true,
    "RegisterAccess": true,
    "RegisterAddAccess": true,
    "RegisterUpdateAccess": true,
    "RegisterBillAccess": true,
    "RegisterIncentiveAccess": true,
    "RegisterPermalinkAccess": true,
    "ClientListAccess": true,
    "ClientUpdateAccess": true,
    "ClientSourceAccess": true,
    "ClientSourceAddAccess": true,
    "ClientSourceUpdateAccess": true,
    "ClientReviewViewAccess": true,
    "ClientReviewUpdateAccess": true,
    "OnboardingAccess": true,
    "OnboardingAddAccess": true,
    "ClientJobDescViewAcces": true,
    "RegisterJobDescriptionUpdateAccess": true,
    "ClientPreQuestioningAccess": true,
    "RegisterPreQuestioningAccess": true,
    "ClientAppreciationViewAcces": true,
    "ClientAppreciationUpdateAccess": true,
    "OffboardingAccess": true,
    "OffboardingAddAccess": true,
    "ClientAllClientAccess": false,
    "TaskPlannerMenuAccess": true,
    "TaskPlannerCreateAccess": true,
    "TaskPlannerDeleteAccess": true,
    "CandidateMenuAccess": true,
    "CandidateAccess": true,
    "CandidateAccess_two": true,
    "CandidateAddAccess": true,
    "CandidateUpdateAccess": true,
    "CandidateApplicationFileUpdateAccess": true,
    "CandidateDeactiveMultipleAccess": true,
    "CandidateResetAccess": true,
    "CandidateDriverDetailsAccess": true,
    "CandidateWorkExperienceAccess": true,
    "CandidateHiredAccess": true,
    "CandidateRejectedAccess": true,
    "CandidateUploadDrugCsvAccess": true,
    "CandidateAddTodayAccess": true,
    "CandidateUploadBulkCVAccess": true,
    "CandidateVerifyTestAccess": true,
    "CandidateModifyClosureAccess": true,
    "CandidateUploadProfileCsvAccess": true,
    "CandidateHireStatusAccess": true,
    "CandidateHireStatusEditAccess": true,
    "CandidateExternalLeadsAccess": true,
    "CandidateResetMailSend": true,
    "CandidateDashboardLinkView": true,
    "CandidateCallBackRequest": true,
    "CandidateShowAllAccess": true,
    "CandidateAllClientAccess": true,
    "CandidateFadvCsvLogRequest": true,
    "CandidateFadvOrderLog": true,
    "CandidateFadvProfileOrderLog": true,
    "CandidateVerifyFADVCsvAccess": true,
    "CandidateBlockFadvRequestorAccess": true,
    "CandidateOnboardingListAccess": true,
    "CandidateConversationAccess": false,
    "CallMenuAccess": true,
    "CallCandidateListAccess": true,
    "CallExcutiveMenuAccess": true,
    "CallExcuSechuledAccess": true,
    "CallExcuVMAccess": true,
    "CallExcuTodayAccess": true,
    "CallExcuCallDetailsAccess": true,
    "CallCallDetailsAccess": true,
    "CallMgrMenuAccess": true,
    "CallMgrOIPAccess": true,
    "CallMgrOEAccess": true,
    "CallMgrFRAccess": true,
    "CallMgrCallDetailsAccess": true,
    "CallMgrMarkEligibleAccess": true,
    "CallMgrResolvedAccess": true,
    "CallMyCall": true,
    "CallDeactiveCandidate": true,
    "CallAVPChnageAccess": true,
    "CallAllClientAccess": false,
    "CallDrugScreeningChangeAccess": true,
    "CallFadvAPIAccess": true,
    "InterviewMenuAccess": true,
    "InterviewFedexDriverAccess": true,
    "ReportMenuAccess": true,
    "ReportInProgressAccess": true,
    "ReportClientAccess": true,
    "ReportSendClientAccess": true,
    "ReportReadyToSendClientAccess": true,
    "ReportRTRInternalAccess": true,
    "ReportEligibleAccess": true,
    "ReportClosureAccess": true,
    "ReportUserAccess": true,
    "ReportCandidateSummaryAccess": true,
    "ReportLeadsAddedAccess": true,
    "ReportClientStatusAccess": true,
    "ReportTeamPerformanceAccess": true,
    "ReportBackEndPerformanceAccess": true,
    "ReportMonthSummaryAccess": true,
    "ReportComplianceAccess": true,
    "NotInterstedReportAccess": true,
    "SegmentCompletionReportAccess": true,
    "CallerPerformanceReportAccess": true,
    "SMEPerformanceReportAccess": true,
    "ReportReadyToSendAccess": true,
    "ReportDrugCSVActivityAccess": true,
    "ReportSessionActivityAccess": true,
    "ReportUserClientActivityAccess": true,
    "ReportAllClientAccess": false,
    "MailScrapMenuAccess": true,
    "ScreeningMailSyncAccess": true,
    "ScreeningMailAccess": true,
    "IncentiveMenuAccess": true,
    "IncentiveMonthlyListAccess": true,
    "IncentiveAchievementsAccess": true,
    "IncentiveGeneratorAccess": true,
    "TeamRecognitionMenuAccess": true,
    "TeamRecognitionMonthlyListAccess": true,
    "TeamRecognitionAchievementsAccess": true,
    "TeamRecognitionGeneratorAccess": true,
    "AIRecruiterMenuAccess": true,
    "AIRecruiterLogAccess": true,
    "CallScheduledReportAccess": true,
    "AuditMenuAccess": true,
    "CallAuditCreateAccess": true,
    "CallAuditAccess": true,
    "CallAuditEditAccess": true,
    "CallAuditEditAccessForAllClient": true,
    "SettingAccess": true,
    "MailTemplateAccess": true,
    "MailTemplateAddAccess": true,
    "MailTemplateUpdateAccess": true,
    "ConversationTemplateAccess": true,
    "ConversationTemplateAddAccess": true,
    "ConversationTemplateUpdateAccess": true,
    "ConversationMatrixAccess": true,
    "ConversationMatrixAddAccess": true,
    "ConversationMatrixUpdateAccess": true,
    "AISmsTemplateAccess": true,
    "AISmsTemplateAddAccess": true,
    "AISmsTemplateUpdateAccess": true,
    "AIAddonCallTemplateAccess": true,
    "AIAddonCallTemplateAddAccess": true,
    "AIAddonCallTemplateUpdateAccess": true,
    "NotificationSettingAccess": true,
    "BroadcastNotificationAccess": true,
    "TrainDataAccess": true,
    "UserIPSettingsAccess": true,
    "TestEmailAccess": true,
    "OverrideMasterSettings": true,
    "UserAccessIPValidation": false
  }
}


export const incentiveType = (statusType) => {
  switch (statusType) {
    case "monthly":
      return `table-status-monthly-stl`
    case "Monthly":
      return `table-status-monthly-stl`
    case "on hire":
      return `table-status-onhire-stl`;
    default:
      return `badge-soft-success`
  }
}

export const quotaType = (quotaType) => {
  switch (quotaType) {
    case "Yes":
      return `table-status-stl`
    case "No":
      return `table-status-onhire-stl`;
    default:
      return `table-status-stl`
  }
}

/** Candidate / client tables: accessors that render as Yes/No quota badges (API booleans or "Yes"/"No" strings). */
const CANDIDATE_TABLE_YES_NO_ACCESSOR_KEYS = new Set([
  "quota",
  "approved",
  "candidatedeactive",
  "fortools",
  "isregister",
  "isbackend",
  "drivingexperience",
  "isfedexdriver",
  "personalitytest",
  "forclient",
  "forjob",
  "ispublished",
  "isacknowledged",
  "haszerotoleranceviolation",
  "published",
  "acknowledged",
  "acknowledgetype",
  "decisiondisplay",
]);

const normalizeAccessorKeyForYesNo = (accessor = "") =>
  String(accessor ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]/g, "");

/**
 * Maps API booleans, 0/1, and Yes/No strings to a display label for quota-style badges.
 * @returns {"Yes"|"No"|null}
 */
export const normalizeYesNoDisplayLabel = (rawValue) => {
  if (rawValue === null || rawValue === undefined || rawValue === "") return null;
  if (typeof rawValue === "boolean") return rawValue ? "Yes" : "No";
  const normalized = String(rawValue).trim().toLowerCase();
  if (normalized === "yes" || normalized === "y" || normalized === "true" || normalized === "1") {
    return "Yes";
  }
  if (normalized === "no" || normalized === "n" || normalized === "false" || normalized === "0") {
    return "No";
  }
  return null;
};

/** Metadata table columns whose API value may live on a sibling field (e.g. `updateSendDate` → `updateSend`). */
const METADATA_TABLE_CELL_VALUE_FALLBACK_ACCESSORS = {
  updateSendDate: ["updateSend"],
};

const resolveMetadataTableRowValueByKey = (row = {}, key = "") => {
  const normalizedKey = String(key ?? "").trim();
  if (!normalizedKey) return undefined;

  if (row?.[normalizedKey] !== undefined && row?.[normalizedKey] !== null) {
    return row[normalizedKey];
  }

  const matchedKey = Object.keys(row).find(
    (rowKey) => String(rowKey).toLowerCase() === normalizedKey.toLowerCase()
  );
  if (matchedKey && row?.[matchedKey] !== undefined && row?.[matchedKey] !== null) {
    return row[matchedKey];
  }

  return undefined;
};

/** Resolve a metadata-driven table cell value from the API row (case-insensitive + known fallbacks). */
export const getMetadataTableCellValue = (row = {}, accessor = "") => {
  const normalizedAccessor = String(accessor ?? "").trim();
  if (!normalizedAccessor || !row || typeof row !== "object") return undefined;

  const directValue = resolveMetadataTableRowValueByKey(row, normalizedAccessor);
  if (directValue !== undefined) return directValue;

  const camelCaseAccessor = pascalToCamel(normalizedAccessor);
  if (camelCaseAccessor !== normalizedAccessor) {
    const camelCaseValue = resolveMetadataTableRowValueByKey(row, camelCaseAccessor);
    if (camelCaseValue !== undefined) return camelCaseValue;
  }

  const fallbackAccessors =
    METADATA_TABLE_CELL_VALUE_FALLBACK_ACCESSORS[normalizedAccessor] ??
    METADATA_TABLE_CELL_VALUE_FALLBACK_ACCESSORS[camelCaseAccessor] ??
    [];

  for (const fallbackAccessor of fallbackAccessors) {
    const fallbackValue = resolveMetadataTableRowValueByKey(row, fallbackAccessor);
    if (fallbackValue !== undefined) return fallbackValue;
  }

  return undefined;
};

/** True when an API/metadata cell value is a boolean that should render as Yes/No. */
export const isBooleanLikeMetadataTableValue = (value) => typeof value === "boolean";

/** Metadata `type` or known accessor → Yes/No quota badge column. */
export const isCandidateTableYesNoColumn = (accessor, columnType) => {
  const normalizedType = String(columnType ?? "")
    .trim()
    .toLowerCase()
    .replace(/[\s_-]/g, "");
  if (["yesno", "boolean", "bool"].includes(normalizedType)) {
    const acc = normalizeAccessorKeyForYesNo(accessor);
    if (["fadvsend", "isfadvsent", "updatesend", "mecstatus", "drugstatus"].includes(acc)) {
      return false;
    }
    return true;
  }
  return CANDIDATE_TABLE_YES_NO_ACCESSOR_KEYS.has(normalizeAccessorKeyForYesNo(accessor));
};

/** Quota-style Yes/No pill; returns null when value is not a yes/no field. */
export const renderCandidateTableYesNoQuotaBadge = (rawValue) => {
  const label = normalizeYesNoDisplayLabel(rawValue);
  if (!label) return null;
  return (
    <span className={`px-2 py-1 fw-semibold common-table-status-badge ${quotaType(label)}`}>{label}</span>
  );
};

/** Shared Yes/No badge for metadata tables when the API value is boolean true/false. */
export const renderMetadataTableBooleanYesNoBadge = (rawValue) => {
  if (!isBooleanLikeMetadataTableValue(rawValue)) return null;
  return renderCandidateTableYesNoQuotaBadge(rawValue);
};

export const citizenType = (item) => {
  switch (item) {
    case "1":
      return `US`
    case "0":
      return `NON US`;
      case "-1":
        return `N/A`;
    default:
      return `N/A`
  }
}

export const statusType = (statusType) => {
  switch (statusType) {
    case "Active":
      return `table-status-stl`
      case "active":
      return `table-status-stl`
    case "Inactive":
      return `table-status-onhire-stl`;
      case "deactive":
      return `table-status-onhire-stl`;
    case "offline":
      return `table-status-onhire-stl`;
    case "Offline":
      return `table-status-onhire-stl`;
      case "block":
      return `format-date-background`;
      case "blocked":
      return `format-date-background`;
      case "yellow":
      return `table-status-yellow`;
      case "pending":
      return `table-status-yellow`;
      case "adpause":
      return `table-status-onhire-stl`;
      case "red":
      return `format-date-background`;
      case "dcie":
      return `format-date-background`;
      case "cnf":
      return `format-date-background`;
      case "enf":
      return `format-date-background`;
      case "apie":
      return `format-date-background`;
      case "success":
      return `badge-soft-success`;
      case "failed":
        return `format-date-background`;
      case "phone number invalid":
      return `badge-soft-success`;
      case "Phone Number Invalid":
      return `badge-soft-success`;
    default:
      return `badge-soft-success`
  }
}

export const statusTypeTrueFalse = (statusType) => {
  switch (statusType) {
    case "True":
      return `table-status-stl`
      case "true":
      return `table-status-stl`
    case "False":
        return `format-date-background`;
      case "false":
        return `format-date-background`;
    default:
      return `badge-soft-success`
  }
}

/**
 * Order in-progress / Under age — map row status text to existing table/badge classes
 * (`quotaType`, `statusType`, `checkBadgeColour`, `table-status-*`, pipeline pills).
 * Returns `null` to render plain `ces-stack-text` (e.g. long free-form labels).
 */
export const resolveCallExecutiveOrderBoardBadgeClass = (raw) => {
  const trimmed = String(raw ?? "").trim();
  if (!trimmed) return null;
  const lower = trimmed.toLowerCase();

  if (lower === "0" || lower === "1") return checkBadgeColour(trimmed);
  if (lower === "yes" || lower === "y") return quotaType("Yes");
  if (lower === "no" || lower === "n") return quotaType("No");

  if (lower.includes("in progress")) return "ces-last-status-pill-primary";
  if (lower.includes("schedule")) return "ces-last-status-pill-primary";
  if (lower.includes("completed") || lower === "complete") return "badge-soft-info";
  if (lower.includes("passed") && !lower.includes("not")) return "table-status-stl";
  if (lower.includes("pending")) return statusType("pending");
  if (lower.includes("not done") || lower.includes("pending result")) return "table-status-yellow";
  if (lower.includes("fail") || lower.includes("denied") || lower.includes("reject")) {
    return "table-status-red";
  }
  if (lower.includes("active")) return statusType("Active");
  if (lower.includes("inactive")) return statusType("Inactive");

  const direct = statusType(trimmed);
  if (direct && direct !== "badge-soft-success") return direct;

  if (lower.includes("paperwork") || lower.includes("paper work")) return null;
  if (lower.includes("non cdl") || lower.length > 48) return null;

  return "badge-soft-secondary";
};

export const onBoardingStatusType = (statusType) => {
  switch (statusType) {
    case "restartclient":
      return `btn-new-client`
      case "Ad Spend byclient":
      return `btn-new-client`
      case "Ad Spend ancileservices":
      return `btn-restart-client`
    default:
      return `btn-restart-client`
  }
}

export const statusKey = (item) => {
  switch (item) {
    case "questionTypeStatus":
      return `status`
      case "jobCategoryStatus":
      return `status`
      case "jobRequireStatus":
      return `status`
      case "QuestionTypeStatus":
      return `status`
      case "status":
      return `status`
    default:
      return ``
  }
}

export const pascalToCamel = (str = "") => {
  return str.charAt(0).toLowerCase() + str.slice(1);
}

export const idKey = (item) => {
  switch (item) {
    case "jobCategoryId":
      return `id`
    case "JobCategoryId":
      return `id`
    default:
      return ``
  }
}

export const timeAgo = (date) => {
  const seconds = Math.floor((new Date() - new Date(date)) / 1000);

  const intervals = [
    { label: "year", seconds: 31536000 },
    { label: "month", seconds: 2592000 },
    { label: "day", seconds: 86400 },
    { label: "hour", seconds: 3600 },
    { label: "minute", seconds: 60 },
    { label: "second", seconds: 1 }
  ];

  for (let i = 0; i < intervals.length; i++) {
    const interval = Math?.floor(seconds / intervals[i].seconds);
    if (interval >= 1) {
      return `${interval} ${intervals[i].label}${interval > 1 ? "s" : ""} ago`;
    }
  }

  return "just now";
};

// ---------------------------------------------------------------------------
// Call Executive — Schedule / Today table (metadata-driven columns + cells)
// Used by TeamTableContainer (`renderCallExecutiveTableBodyCell`) and pages.
// ---------------------------------------------------------------------------

/** Maps API `callStatus` to `theme.scss` legend/name suffixes (`ces-cand-*--*` ). */
export const getLegendAndNameVariantSuffixFromCallStatus = (callStatusRaw) => {
  const normalized = String(callStatusRaw ?? "")
    .trim()
    .toLowerCase();
  if (!normalized) return "default";

  if (normalized.includes("vm") && !normalized.includes("schedule")) {
    return "vm";
  }
  if (normalized.includes("schedule")) {
    return "scheduled-drug-required";
  }
  if (normalized.includes("eligible")) {
    return "drug-eligible";
  }
  if (normalized.includes("progress") || normalized.includes("mint")) {
    return "drug-order-mint";
  }
  if (
    (normalized.includes("not") && normalized.includes("call")) ||
    normalized.includes("new lead")
  ) {
    return "not-called";
  }
  return "default";
};

/** Wrong-number page: apply `table-status-red` only when `callStatus` equals this (case-insensitive). */
export const isWrongNumberPhoneInvalidCallStatus = (raw) =>
  String(raw ?? "").trim().toLowerCase() === "phone number invalid";

export const isUnderAgeInvalidCallStatus = (raw) =>
  String(raw ?? "").trim().toLowerCase() === "candidate is over/under age";

/** Call Executive datetime column display: `h:mm A MM-DD-YYYY`. */
export const formatCallExecutiveDateTimeForTable = (isoOrDateValue) => {
  if (isoOrDateValue === null || isoOrDateValue === undefined || isoOrDateValue === "") {
    return "—";
  }
  const parsed = dayjs(isoOrDateValue);
  if (!parsed.isValid()) {
    return String(isoOrDateValue);
  }
  return `${parsed.format("h:mm A")} ${parsed.format("MM-DD-YYYY")}`;
};

/** Title-case labels for profile/order/status text in Call Executive cells. */
export const formatCallExecutiveTitleCaseDisplay = (rawValue) => {
  if (rawValue === null || rawValue === undefined || rawValue === "") {
    return "—";
  }
  return String(rawValue)
    .trim()
    .split(/\s+/)
    .map((word) => {
      if (!word) return word;
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
};

/**
 * Maps Job Acceptance / Yes–No strings to the same classes used across tables (`quotaType`, `statusType`).
 * @returns {{ displayLabel: string, statusClasses: string|null }} — `statusClasses` null means plain muted text (empty).
 */
export const getJobAcceptanceBadgePresentation = (jobAcceptanceRaw) => {
  const normalized = String(jobAcceptanceRaw ?? "")
    .trim()
    .toLowerCase();
  const displayLabel =
    jobAcceptanceRaw === null || jobAcceptanceRaw === undefined || jobAcceptanceRaw === ""
      ? "—"
      : String(jobAcceptanceRaw).trim();

  if (!normalized) {
    return { displayLabel: "—", statusClasses: null };
  }
  if (normalized === "yes" || normalized === "y") {
    return { displayLabel: displayLabel || "Yes", statusClasses: quotaType("Yes") };
  }
  if (normalized === "no" || normalized === "n") {
    return { displayLabel: displayLabel || "No", statusClasses: quotaType("No") };
  }
  if (normalized === "pending") {
    return {
      displayLabel: displayLabel || "pending",
      statusClasses: statusType("pending"),
    };
  }
  return {
    displayLabel,
    statusClasses: quotaType("Yes"),
  };
};

/** When the API sends plain Yes/No (any casing), use `quotaType` like TeamTableContainer quota columns. */
export const renderCallExecutiveYesNoQuotaBadgeOrNull = (rawValue) => {
  const normalized = String(rawValue ?? "").trim().toLowerCase();
  if (normalized === "yes" || normalized === "y") {
    return (
      <span className={`px-2 py-1 fw-semibold ${quotaType("Yes")}`}>Yes</span>
    );
  }
  if (normalized === "no" || normalized === "n") {
    return (
      <span className={`px-2 py-1 fw-semibold ${quotaType("No")}`}>No</span>
    );
  }
  return null;
};

/**
 * Merges metadata columns so Profile+Order and Last Status match the executive UI
 * (called from today.jsx / schedule.jsx when building column definitions).
 *
 * @param {object} [options]
 * @param {boolean} [options.mergeProfileAndOrder=true] — Order in-progress / Under age pass `false`
 * for separate badge columns like the screenshot.
 * @param {boolean} [options.mergeCallStatusAndSchedule=true]
 */
export const normalizeMetadataColumnAccessor = (accessor) =>
  String(accessor ?? "")
    .trim()
    .replace(/[_\s-]/g, "")
    .toLowerCase();

export const isMetadataColumnAccessor = (accessor, expectedAccessor) =>
  normalizeMetadataColumnAccessor(accessor) ===
  normalizeMetadataColumnAccessor(expectedAccessor);

/** API / metadata may use `profileOrderStatus` or legacy `profileAndOrderStatus`. */
export const isProfileOrderCombinedColumnAccessor = (accessor) =>
  isMetadataColumnAccessor(accessor, "profileOrderStatus") ||
  isMetadataColumnAccessor(accessor, "profileAndOrderStatus");

export const buildCallExecutiveTableColumnsFromMetadata = (
  metadataColumns,
  options = {}
) => {
  const mergeProfileAndOrder = options.mergeProfileAndOrder !== false;
  const mergeCallStatusAndSchedule = options.mergeCallStatusAndSchedule !== false;

  if (!Array.isArray(metadataColumns) || metadataColumns.length === 0) {
    return [];
  }

  const accessors = metadataColumns.map((column) => column.accessor).filter(Boolean);
  const hasProfileStatus = accessors.some((accessor) =>
    isMetadataColumnAccessor(accessor, "profileStatus")
  );
  const hasOrderStatus = accessors.some((accessor) =>
    isMetadataColumnAccessor(accessor, "orderStatus")
  );
  const hasCallStatus = accessors.some((accessor) =>
    isMetadataColumnAccessor(accessor, "callStatus")
  );
  const hasScheduleDateTime = accessors.some((accessor) =>
    isMetadataColumnAccessor(accessor, "scheduleDateTime")
  );
  const hasProfileAndOrderStatus = accessors.some((accessor) =>
    isProfileOrderCombinedColumnAccessor(accessor)
  );

  let result = metadataColumns.map((column) => ({ ...column }));

  if (mergeProfileAndOrder && (hasProfileStatus || hasOrderStatus || hasProfileAndOrderStatus)) {
    if (hasProfileAndOrderStatus) {
      result = result.filter(
        (column) =>
          !isMetadataColumnAccessor(column.accessor, "orderStatus") &&
          !isMetadataColumnAccessor(column.accessor, "profileStatus")
      );
      result = result.map((column) =>
        isProfileOrderCombinedColumnAccessor(column.accessor)
          ? {
              ...column,
              Header: column.Header || "Profile & Order Status",
              accessor: "profileOrderStatus",
            }
          : column
      );
    } else {
      const insertIndex = result.findIndex(
        (column) =>
          isMetadataColumnAccessor(column.accessor, "profileStatus") ||
          isMetadataColumnAccessor(column.accessor, "orderStatus")
      );
      const sourceColumn =
        result.find((column) => isMetadataColumnAccessor(column.accessor, "profileStatus")) ||
        result.find((column) => isMetadataColumnAccessor(column.accessor, "orderStatus"));

      result = result.filter(
        (column) =>
          !isMetadataColumnAccessor(column.accessor, "orderStatus") &&
          !isMetadataColumnAccessor(column.accessor, "profileStatus")
      );

      const mergedColumn = {
        ...sourceColumn,
        Header: "Profile & Order Status",
        accessor: "profileOrderStatus",
      };

      if (insertIndex >= 0) {
        result.splice(insertIndex, 0, mergedColumn);
      } else {
        result.push(mergedColumn);
      }
    }
  }

  if (mergeCallStatusAndSchedule && hasCallStatus && hasScheduleDateTime) {
    result = result.filter(
      (column) => !isMetadataColumnAccessor(column.accessor, "scheduleDateTime")
    );
    result = result.map((column) =>
      isMetadataColumnAccessor(column.accessor, "callStatus")
        ? {
            ...column,
            Header: "Last Status",
            accessor: "lastStatusStack",
          }
        : column
    );
  }

  return result;
};

const looksLikeHtmlString = (value) =>
  value != null && /<[a-z][\s\S]*>/i.test(String(value));

const resolveCallExecutiveStatusHtmlContent = (
  rowRecord,
  getItemValueByAccessor,
  statusKey
) => {
  const htmlKey = `${statusKey}Html`;
  const explicitHtml = getItemValueByAccessor(rowRecord, htmlKey);
  if (explicitHtml != null && String(explicitHtml).trim() !== "") {
    return String(explicitHtml);
  }
  const raw = getItemValueByAccessor(rowRecord, statusKey);
  if (looksLikeHtmlString(raw)) return String(raw);
  return "";
};

const renderCallExecutiveHtmlStatusLine = (html, extraClass = "") => {
  const trimmed = String(html ?? "").trim();
  if (!trimmed) return null;
  return (
    <span
      className={[
        "ces-profile-order-line",
        "common-candidate-table-html-dangerous",
        extraClass,
      ]
        .filter(Boolean)
        .join(" ")}
      dangerouslySetInnerHTML={{ __html: trimmed }}
    />
  );
};

const resolveCallExecutiveCombinedProfileOrderHtml = (
  rowRecord,
  getItemValueByAccessor
) => {
  const profileOrderKeys = ["profileOrderStatus", "profileAndOrderStatus"];
  for (let index = 0; index < profileOrderKeys.length; index += 1) {
    const html = resolveCallExecutiveStatusHtmlContent(
      rowRecord,
      getItemValueByAccessor,
      profileOrderKeys[index]
    );
    if (html) return html;
  }
  return "";
};

const getSyntheticCallExecutiveFieldFromRow = (rowRecord, accessor) => {
  if (isProfileOrderCombinedColumnAccessor(accessor)) {
    const combined =
      rowRecord?.profileOrderStatus ?? rowRecord?.profileAndOrderStatus ?? "";
    if (combined != null && String(combined).trim() !== "") {
      return combined;
    }
    return rowRecord?.profileStatus ?? rowRecord?.orderStatus ?? "";
  }
  if (isMetadataColumnAccessor(accessor, "lastStatusStack")) {
    return rowRecord?.callStatus ?? "";
  }
  return undefined;
};

/** Cell scalar lookup including synthetic accessors after column merge. */
export const getCallExecutiveCellRawValue = (rowRecord, accessor, getValueByAccessor) => {
  if (!rowRecord || !accessor) return "";
  const synthetic = getSyntheticCallExecutiveFieldFromRow(rowRecord, accessor);
  if (synthetic !== undefined) return synthetic;
  return getValueByAccessor(rowRecord, accessor);
};

const stripHtmlToPlainText = (value) => {
  const raw = String(value ?? "");
  if (!raw.trim()) return "";
  return raw
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(?:div|p|li|tr)>/gi, "\n")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/[ \t]+\n/g, "\n")
    .replace(/\n[ \t]+/g, "\n")
    .replace(/[ \t]{2,}/g, " ")
    .replace(/\n{2,}/g, "\n")
    .trim();
};

/**
 * Plain-text cell values for Call Executive COPY/CSV/Excel/PDF.
 * Matches stacked on-screen cells (Last Status + schedule datetime, Profile & Order, etc.).
 */
export const resolveCallExecutiveExportCellValue = (
  rowRecord,
  accessor,
  getValueByAccessor
) => {
  if (!rowRecord || !accessor || typeof getValueByAccessor !== "function") {
    return "";
  }

  if (isMetadataColumnAccessor(accessor, "lastStatusStack")) {
    const callStatusLabel =
      getValueByAccessor(rowRecord, "callStatus") ||
      getValueByAccessor(rowRecord, "statusLabel") ||
      "";
    const scheduleDateTimeValue = getValueByAccessor(rowRecord, "scheduleDateTime");
    const formattedSchedule = formatCallExecutiveDateTimeForTable(scheduleDateTimeValue);
    const statusText = stripHtmlToPlainText(callStatusLabel);
    const scheduleText =
      formattedSchedule && formattedSchedule !== "—" ? formattedSchedule : "";
    const rawVmCount =
      getValueByAccessor(rowRecord, "vmCount") ?? rowRecord?.vmCount;
    const vmCountNumber = Number(rawVmCount);
    const isVmPrimaryStatus =
      getLegendAndNameVariantSuffixFromCallStatus(callStatusLabel) === "vm";
    const vmCountText =
      !isVmPrimaryStatus ||
      rawVmCount === null ||
      rawVmCount === undefined ||
      rawVmCount === "" ||
      !Number.isFinite(vmCountNumber) ||
      vmCountNumber === 0
        ? ""
        : `Total : ${vmCountNumber}`;
    return [statusText, scheduleText, vmCountText].filter(Boolean).join("\n");
  }

  if (isProfileOrderCombinedColumnAccessor(accessor)) {
    const combinedHtml = resolveCallExecutiveCombinedProfileOrderHtml(
      rowRecord,
      getValueByAccessor
    );
    if (combinedHtml) {
      return stripHtmlToPlainText(combinedHtml);
    }
    const profileHtml = resolveCallExecutiveStatusHtmlContent(
      rowRecord,
      getValueByAccessor,
      "profileStatus"
    );
    const orderHtml = resolveCallExecutiveStatusHtmlContent(
      rowRecord,
      getValueByAccessor,
      "orderStatus"
    );
    if (profileHtml || orderHtml) {
      return [stripHtmlToPlainText(profileHtml), stripHtmlToPlainText(orderHtml)]
        .filter(Boolean)
        .join("\n");
    }
    const profileStatusText = formatCallExecutiveTitleCaseDisplay(
      getValueByAccessor(rowRecord, "profileStatus")
    );
    const orderStatusText = formatCallExecutiveTitleCaseDisplay(
      getValueByAccessor(rowRecord, "orderStatus")
    );
    return [profileStatusText, orderStatusText]
      .filter((part) => part && part !== "—")
      .join("\n");
  }

  if (accessor === "candidateName") {
    const name =
      getValueByAccessor(rowRecord, "candidateName") ||
      getValueByAccessor(rowRecord, "name") ||
      "";
    const postApplied = getValueByAccessor(rowRecord, "postApplied");
    const postAppliedStr =
      postApplied != null && String(postApplied).trim() !== ""
        ? formatCallExecutiveTitleCaseDisplay(String(postApplied))
        : "";
    const nameText = String(name ?? "").trim();
    if (nameText && postAppliedStr) return `${nameText}\n${postAppliedStr}`;
    return nameText || postAppliedStr || "";
  }

  const raw = getCallExecutiveCellRawValue(rowRecord, accessor, getValueByAccessor);

  if (
    accessor === "lastCallDateTime" ||
    accessor === "dateAdded" ||
    accessor === "updateSent" ||
    accessor === "scheduleDateTime" ||
    String(accessor).toLowerCase().includes("datetime")
  ) {
    const formatted = formatCallExecutiveDateTimeForTable(raw);
    return formatted === "—" ? "" : formatted;
  }

  const normalizedAccessor = String(accessor ?? "")
    .toLowerCase()
    .replace(/[^a-z]/g, "");
  if (
    normalizedAccessor.includes("phone") ||
    normalizedAccessor === "mobile" ||
    normalizedAccessor === "cell"
  ) {
    return phoneNumberShowFormat(raw) || String(raw ?? "").trim() || "";
  }

  if (looksLikeHtmlString(raw)) {
    return stripHtmlToPlainText(raw);
  }

  if (raw === null || raw === undefined) return "";
  return String(raw);
};

/** True when this accessor should render the editable Fedex ID textarea in Call Executive tables. */
export const isCallExecutiveFedexIdAccessor = (accessor) => {
  const normalized = String(accessor ?? "").toLowerCase().replace(/[^a-z]/g, "");
  return normalized.includes("fedexid");
};

/** True when this accessor should render the quick-update textarea in Call Executive tables. */
export const isCallExecutiveQuickUpdateAccessor = (accessor) => {
  const normalized = String(accessor ?? "").toLowerCase();
  return (
    normalized.includes("quick") ||
    normalized.includes("callupdate") ||
    normalized === "notes" ||
    normalized === "callnotes"
  );
};

/** Ensures the last column is Call/ Quick Update when metadata omits it. */
export const appendCallExecutiveQuickUpdateColumnIfMissing = (columns = []) => {
  const list = Array.isArray(columns) ? [...columns] : [];
  const hasQuickUpdateColumn = list.some((column) =>
    isCallExecutiveQuickUpdateAccessor(column?.accessor)
  );
  if (hasQuickUpdateColumn) return list;
  return [
    ...list,
    {
      Header: "Call/ Quick Update",
      accessor: "callQuickUpdate",
      type: "textarea",
    },
  ];
};

export const resolveCallExecutiveQuickUpdateCandidateId = (rowRecord = {}) =>
  String(rowRecord?.candidateId ?? rowRecord?.id ?? rowRecord?.Id ?? "").trim();

export const resolveCallExecutiveQuickUpdateCandidateClientId = (rowRecord = {}) =>
  String(
    rowRecord?.candidateClientId ??
      rowRecord?.CandidateClientId ??
      ""
  ).trim();

export const resolveCallExecutiveQuickUpdateRowValue = (
  rowRecord = {},
  fieldAccessor = "callQuickUpdate"
) => {
  if (fieldAccessor && rowRecord?.[fieldAccessor] != null) {
    return String(rowRecord[fieldAccessor]);
  }
  return String(
    rowRecord?.callQuickUpdate ??
      rowRecord?.quickUpdate ??
      rowRecord?.callQuickUpdateNotes ??
      ""
  );
};

/**
 * Phone column: prefer formatted display string from API; skip blank strings so `phone: null`
 * does not hide `candidatePhoneFormat`.
 */
export const resolveCallExecutivePhoneDisplayValue = (rowRecord, getItemValueByAccessor) => {
  const keysInPriorityOrder = [
    "candidatePhoneFormat",
    "phone",
    "candidatePhone",
    "mobile",
  ];
  for (let index = 0; index < keysInPriorityOrder.length; index += 1) {
    const key = keysInPriorityOrder[index];
    const value = getItemValueByAccessor(rowRecord, key);
    if (value !== null && value !== undefined && String(value).trim() !== "") {
      return String(value).trim();
    }
  }
  return "";
};

/**
 * Renders one `<td>` body contents for Call Executive Schedule / Today / Wrong number inside TeamTableContainer.
 *
 * @param {object} params.columnDefinition — `{ Header, accessor, type }`
 * @param {object} params.rowRecord — API row (camelCase)
 * @param {number} params.rowIndex
 * @param {Function} [params.onQuickUpdateBlur] — `(row, value, accessor) => void`
 * @param {Function} params.getItemValueByAccessor — TeamTableContainer field resolver
 * @param {string} [params.pageName] — e.g. `CallExecutiveWrongNumber` for red status styling
 */
export const renderCallExecutiveTableBodyCell = ({
  columnDefinition,
  rowRecord,
  rowIndex,
  onQuickUpdateBlur,
  getItemValueByAccessor,
  pageName = "",
}) => {
  const pageNameNorm = String(pageName || "").toLowerCase();
  const isWrongNumberExecutivePage = pageNameNorm === "callexecutivewrongnumber";
  const isReadyToSendManagerPage = pageNameNorm === "callmanagerreadytosend";
  const isUnderAgeExecutivePage = pageNameNorm === "callexecutiveunderage";
  const isMyCallOrDeactivateExecutivePage =
    pageNameNorm === "callexecutivemycall" ||
    pageNameNorm === "callexecutivedeactivatecandidate";
  const isOrderPipelineExecutivePage =
    pageNameNorm === "callexecutiveinprogress" ||
    pageNameNorm === "callexecutiveunderage";
  const isScheduleStyleProfileOrderPage =
    pageNameNorm === "callexecutiveschedule" ||
    pageNameNorm === "callexecutivenotonai" ||
    pageNameNorm === "callexecutivetoday" ||
    pageNameNorm === "callexecutiveinterview";

  /** Order in-progress / Under age — pill colors via `resolveCallExecutiveOrderBoardBadgeClass`; plain text when `null`. */
  const renderOrderPipelineStatusValue = (cellValue, statusAccessor = "orderStatus") => {
    if (cellValue === null || cellValue === undefined || String(cellValue).trim() === "") {
      return <span className="text-muted">—</span>;
    }
    const str = String(cellValue);
    const display = formatCallExecutiveTitleCaseDisplay(str);
    const cls = resolveCallExecutiveOrderBoardBadgeClass(str);
    const metadataPresentation = resolveStatusBadgePresentationExternal({
      accessor: statusAccessor,
      pageName,
      value: str,
      fallbackClassName: cls || "",
    });
    if (!metadataPresentation?.inlineStyle && !cls) {
      return <span className="ces-stack-text">{display}</span>;
    }
    return renderStatusMetadataPill(str, {
      accessor: statusAccessor,
      pageName,
      className: "px-2 py-1 fw-semibold d-inline-block",
      fallbackClassName: cls || "",
    });
  };

  const accessor = columnDefinition?.accessor;
  const callStatusValue = getItemValueByAccessor(rowRecord, "callStatus");

  const rawValueForColumn = getCallExecutiveCellRawValue(
    rowRecord,
    accessor,
    getItemValueByAccessor
  );

  if (isMetadataLinkTableColumnType(columnDefinition?.type)) {
    const columnTitle =
      typeof columnDefinition?.Header === "string" && columnDefinition.Header.trim()
        ? columnDefinition.Header.trim()
        : String(columnDefinition?.keyName ?? accessor ?? "Link");
    return (
      <MetadataLinkTableCell
        url={rawValueForColumn}
        columnTitle={columnTitle}
      />
    );
  }

  const variantSuffix = getLegendAndNameVariantSuffixFromCallStatus(callStatusValue);

  /**
   * Ready To Send (manager) — status columns reuse the All Candidates design: API-driven colored
   * status pills via `renderStatusMetadataPill` (same status-metadata source as `StatusMetadataBadge`).
   */
  if (isReadyToSendManagerPage) {
    const columnType = String(columnDefinition?.type ?? "").trim().toLowerCase();
    const accessorNormalized = String(accessor ?? "").replace(/_/g, "").toLowerCase();
    const isReadyToSendStatusColumn =
      columnType === "status" ||
      [
        "callstatus",
        "orderstatus",
        "profilestatus",
        "readytosend",
        "reportsend",
        "drugstatus",
        "mecstatus",
        "personalitytest",
        "jobacceptance",
        "statuslabel",
        "status",
      ].includes(accessorNormalized);
    if (isReadyToSendStatusColumn) {
      return renderStatusMetadataPill(rawValueForColumn, {
        accessor,
        pageName,
        fallbackClassName: statusType(String(rawValueForColumn ?? "")),
      });
    }
  }

  if (
    isOrderPipelineExecutivePage &&
    (isMetadataColumnAccessor(accessor, "profileStatus") ||
      isMetadataColumnAccessor(accessor, "orderStatus"))
  ) {
    return renderOrderPipelineStatusValue(rawValueForColumn);
  }

  if (accessor === "clientName") {
    const companyName =
      getItemValueByAccessor(rowRecord, "clientName") ||
      getItemValueByAccessor(rowRecord, "company") ||
      "";
    return (
      <span
        className={`ces-stack-text text-body${
          isUnderAgeExecutivePage ? " ces-call-exec-under-age-client" : ""
        }${isMyCallOrDeactivateExecutivePage ? " fw-semibold" : ""}`}
      >
        {companyName || "—"}
      </span>
    );
  }

  if (
    isMyCallOrDeactivateExecutivePage &&
    (accessor === "jobName" || accessor === "job")
  ) {
    if (
      rawValueForColumn === null ||
      rawValueForColumn === undefined ||
      String(rawValueForColumn).trim() === ""
    ) {
      return <span className="text-muted">—</span>;
    }
    return (
      <span className="ces-stack-text ces-call-exec-my-deactivate-job-name">
        {formatCallExecutiveTitleCaseDisplay(String(rawValueForColumn))}
      </span>
    );
  }

  if (accessor === "candidateName") {
    const candidateFullName =
      getItemValueByAccessor(rowRecord, "candidateName") ||
      getItemValueByAccessor(rowRecord, "name") ||
      "";

    const candidateColorRaw =
      getItemValueByAccessor(rowRecord, "candidateColor") ??
      getItemValueByAccessor(rowRecord, "CandidateColor");
    const candidateColor =
      candidateColorRaw != null && String(candidateColorRaw).trim() !== ""
        ? String(candidateColorRaw).trim()
        : null;

    const jobColorRaw =
      getItemValueByAccessor(rowRecord, "jobColor") ??
      getItemValueByAccessor(rowRecord, "JobColor");
    const jobColor =
      jobColorRaw != null && String(jobColorRaw).trim() !== ""
        ? String(jobColorRaw).trim()
        : null;

    const nameClass = candidateColor
      ? "ces-cand-name ces-candidate-name-line"
      : variantSuffix === "default"
        ? "ces-cand-name ces-cand-name--default ces-candidate-name-line"
        : `ces-cand-name ces-cand-name--${variantSuffix} ces-candidate-name-line`;

    const nameStyle = candidateColor ? { color: candidateColor } : undefined;

    const jobSubtitleClass =
      variantSuffix === "default"
        ? "ces-cand-job ces-cand-job--default"
        : `ces-cand-job ces-cand-job--${variantSuffix}`;

    const postAppliedRaw = getItemValueByAccessor(rowRecord, "postApplied");
    const postAppliedStr =
      postAppliedRaw != null && String(postAppliedRaw).trim() !== ""
        ? formatCallExecutiveTitleCaseDisplay(String(postAppliedRaw))
        : "";

    const subtitleClassName = jobColor
      ? "ces-cand-job ces-candidate-subtitle-line text-break"
      : `${jobSubtitleClass} ces-candidate-subtitle-line text-break`;
    const subtitleStyle = jobColor ? { color: jobColor } : undefined;

    return (
      <div className="ces-candidate-name-wrap">
        <div className={nameClass} style={nameStyle}>
          {candidateFullName || "—"}
        </div>
        {postAppliedStr ? (
          <div className={subtitleClassName} style={subtitleStyle}>
            {postAppliedStr}
          </div>
        ) : null}
      </div>
    );
  }

  const renderProfileAndOrderStatusStack = () => {
    const profileStatusText = getItemValueByAccessor(rowRecord, "profileStatus");
    const orderStatusText = getItemValueByAccessor(rowRecord, "orderStatus");

    if (isScheduleStyleProfileOrderPage) {
      const combinedHtml = resolveCallExecutiveCombinedProfileOrderHtml(
        rowRecord,
        getItemValueByAccessor
      );
      if (combinedHtml) {
        return (
          <div className="ces-profile-order-stack">
            {renderCallExecutiveHtmlStatusLine(combinedHtml, "ces-stack-text")}
          </div>
        );
      }

      const profileHtml = resolveCallExecutiveStatusHtmlContent(
        rowRecord,
        getItemValueByAccessor,
        "profileStatus"
      );
      const orderHtml = resolveCallExecutiveStatusHtmlContent(
        rowRecord,
        getItemValueByAccessor,
        "orderStatus"
      );

      if (profileHtml || orderHtml) {
        return (
          <div className="ces-profile-order-stack">
            {profileHtml
              ? renderCallExecutiveHtmlStatusLine(profileHtml, "ces-stack-text")
              : (
                <span className="ces-profile-order-line ces-stack-text text-muted">—</span>
              )}
            {orderHtml
              ? renderCallExecutiveHtmlStatusLine(orderHtml, "ces-stack-text text-muted")
              : (
                <span className="ces-profile-order-line ces-stack-text text-muted">—</span>
              )}
          </div>
        );
      }
    }

    if (isOrderPipelineExecutivePage) {
      return (
        <div className="ces-profile-order-stack">
          <span className="ces-profile-order-line d-block">
            {renderOrderPipelineStatusValue(profileStatusText, "profileStatus")}
          </span>
          <span className="ces-profile-order-line d-block">
            {renderOrderPipelineStatusValue(orderStatusText, "orderStatus")}
          </span>
        </div>
      );
    }
    return (
      <div className="ces-profile-order-stack">
        <span className="ces-profile-order-line ces-stack-text">
          {formatCallExecutiveTitleCaseDisplay(profileStatusText)}
        </span>
        <span className="ces-profile-order-line ces-stack-text text-muted">
          {formatCallExecutiveTitleCaseDisplay(orderStatusText)}
        </span>
      </div>
    );
  };

  if (isProfileOrderCombinedColumnAccessor(accessor)) {
    return renderProfileAndOrderStatusStack();
  }

  if (
    isMetadataColumnAccessor(accessor, "profileStatus") &&
    isScheduleStyleProfileOrderPage
  ) {
    return renderProfileAndOrderStatusStack();
  }

  if (
    isMetadataColumnAccessor(accessor, "orderStatus") &&
    isScheduleStyleProfileOrderPage
  ) {
    return renderProfileAndOrderStatusStack();
  }

  if (accessor === "jobAcceptance") {
    const presentation = getJobAcceptanceBadgePresentation(
      getItemValueByAccessor(rowRecord, "jobAcceptance")
    );
    if (presentation.displayLabel === "—" || presentation.statusClasses == null) {
      return <span className="text-muted">—</span>;
    }
    return (
      <span className={`px-2 py-1 fw-semibold ${presentation.statusClasses}`}>
        {presentation.displayLabel}
      </span>
    );
  }

  /**
   * MEC / drug / personality — must run before My Call `statusType` branch so accessors like `status`
   * never steal rendering; same markup as Schedule (`CallExecutiveSchedule`) for non–order-pipeline pages.
   */
  if (accessor === "mecStatus" || accessor === "drugStatus" || accessor === "personalityTest") {
    const text = rawValueForColumn;
    if (text === null || text === undefined || text === "") {
      return <span className="text-muted">—</span>;
    }
    const yesNoBadge = renderCallExecutiveYesNoQuotaBadgeOrNull(text);
    if (yesNoBadge) return yesNoBadge;
    if (isOrderPipelineExecutivePage) {
      return renderOrderPipelineStatusValue(text);
    }
    return (
      <span className="ces-stack-text">{formatCallExecutiveTitleCaseDisplay(String(text))}</span>
    );
  }

  /**
   * My Call & Deactivate Candidate — same status pill markup as Team (`TeamTableContainer`:
   * `px-2 py-1 fw-semibold` + `statusType(value)`; team.jsx list uses `CommonTableForClient` / `statusType` parity).
   * Excludes mec/drug/personality columns so they stay aligned with Schedule.jsx.
   */
  if (
    isMyCallOrDeactivateExecutivePage &&
    accessor !== "mecStatus" &&
    accessor !== "drugStatus" &&
    accessor !== "personalityTest" &&
    (accessor === "status" ||
      accessor === "callStatus" ||
      accessor === "statusLabel" ||
      accessor === "lastStatus")
  ) {
    if (
      rawValueForColumn === null ||
      rawValueForColumn === undefined ||
      String(rawValueForColumn).trim() === ""
    ) {
      return <span className="text-muted">—</span>;
    }
    const cellValue = String(rawValueForColumn).trim();
    return renderStatusMetadataPill(cellValue, {
      accessor,
      pageName,
      fallbackClassName: statusType(cellValue),
    });
  }

  /** Under-age page: same status treatment as wrong-number; display text from `statusLabel`. */
  if (
    (accessor === "callStatus" || accessor === "status" || accessor === "statusLabel") &&
    isUnderAgeExecutivePage
  ) {
    const statusText =
      getItemValueByAccessor(rowRecord, "statusLabel") ||
      getItemValueByAccessor(rowRecord, "status") ||
      getItemValueByAccessor(rowRecord, "callStatus");
    if (
      statusText === null ||
      statusText === undefined ||
      String(statusText).trim() === ""
    ) {
      return <span className="text-muted">—</span>;
    }
    const display = formatCallExecutiveTitleCaseDisplay(String(statusText));
    const useRed = isWrongNumberPhoneInvalidCallStatus(statusText) || isUnderAgeInvalidCallStatus(statusText);
    return (
      <div className="ces-wrong-number-status-zone">
        {useRed ? (
          renderStatusMetadataPill(statusText, {
            accessor: "callStatus",
            pageName,
            className: "px-2 py-1 fw-semibold format-date-background d-inline-block",
            fallbackClassName: "format-date-background",
          })
        ) : (
          <span className="ces-stack-text">{display}</span>
        )}
      </div>
    );
  }

  /** Wrong-number page only: red badge when `callStatus` is "phone number invalid" (not whole `<td>`). */
  if ((accessor === "callStatus" || accessor === "statusLabel") && isWrongNumberExecutivePage) {
    const callStatusLabel = getItemValueByAccessor(rowRecord, "callStatus");
    if (
      callStatusLabel === null ||
      callStatusLabel === undefined ||
      String(callStatusLabel).trim() === ""
    ) {
      return <span className="text-muted">—</span>;
    }
    const display = formatCallExecutiveTitleCaseDisplay(String(callStatusLabel));
    const useRed = isWrongNumberPhoneInvalidCallStatus(callStatusLabel);
    return (
      <div className="ces-wrong-number-status-zone">
        {useRed ? (
          renderStatusMetadataPill(callStatusLabel, {
            accessor: "callStatus",
            pageName,
            className: "px-2 py-1 fw-semibold format-date-background d-inline-block",
            fallbackClassName: "format-date-background",
          })
        ) : (
          <span className="ces-stack-text">{display}</span>
        )}
      </div>
    );
  }

  if (isMetadataColumnAccessor(accessor, "lastStatusStack")) {
    const callStatusLabel =
      getItemValueByAccessor(rowRecord, "callStatus") ||
      getItemValueByAccessor(rowRecord, "statusLabel") ||
      "";
    const scheduleDateTimeValue = getItemValueByAccessor(rowRecord, "scheduleDateTime");
    const formattedSchedule = formatCallExecutiveDateTimeForTable(scheduleDateTimeValue);
    const rawVmCount =
      getItemValueByAccessor(rowRecord, "vmCount") ?? rowRecord?.vmCount;
    const vmCountNumber = Number(rawVmCount);
    const isVmPrimaryStatus =
      getLegendAndNameVariantSuffixFromCallStatus(callStatusLabel) === "vm";
    const showVmTotal =
      (pageNameNorm === "callexecutivetoday" ||
        pageNameNorm === "callexecutiveschedule" ||
        pageNameNorm === "callexecutivenotonai") &&
      isVmPrimaryStatus &&
      rawVmCount !== null &&
      rawVmCount !== undefined &&
      String(rawVmCount).trim() !== "" &&
      Number.isFinite(vmCountNumber) &&
      vmCountNumber > 0;

    const primaryStatusNode =
      isWrongNumberExecutivePage && isWrongNumberPhoneInvalidCallStatus(callStatusLabel)
        ? renderStatusMetadataPill(callStatusLabel, {
            accessor: "callStatus",
            pageName,
            className: "px-2 py-1 fw-semibold table-status-red d-inline-block",
            fallbackClassName: "table-status-red",
          })
        : isWrongNumberExecutivePage
          ? (
              <span className="ces-stack-text">
                {formatCallExecutiveTitleCaseDisplay(callStatusLabel)}
              </span>
            )
          : isReadyToSendManagerPage
            ? renderStatusMetadataPill(callStatusLabel, {
                accessor: "callStatus",
                pageName,
                fallbackClassName: statusType(String(callStatusLabel ?? "")),
              })
            : renderStatusMetadataPill(callStatusLabel, {
                accessor: "callStatus",
                pageName,
                className: "px-2 py-1 fw-semibold ces-last-status-pill-primary d-inline-block",
                fallbackClassName: "ces-last-status-pill-primary",
              });

    return (
      <div
        className={
          isWrongNumberExecutivePage
            ? "ces-last-status-compact ces-wrong-number-status-zone"
            : isOrderPipelineExecutivePage
              ? "ces-last-status-compact ces-order-pipeline-status-stack"
              : isReadyToSendManagerPage
                ? "ces-last-status-compact ces-ready-to-send-last-status"
                : "ces-last-status-compact"
        }
      >
        {primaryStatusNode}
        {showVmTotal ? (
          <span className="px-2 py-1 fw-semibold ces-last-status-pill-vm-total d-inline-block">
            Total : {vmCountNumber}
          </span>
        ) : null}
        <span className="ces-last-status-pill-datetime">
          {formattedSchedule === "—" ? "—" : formattedSchedule}
        </span>
      </div>
    );
  }

  if (
    accessor === "videoInterview" &&
    isOrderPipelineExecutivePage
  ) {
    return renderOrderPipelineStatusValue(rawValueForColumn);
  }

  if (
    accessor === "lastCallDateTime" ||
    accessor === "dateAdded" ||
    accessor === "updateSent" ||
    String(accessor).toLowerCase().includes("datetime")
  ) {
    return (
      <span className="ces-datetime">
        {formatCallExecutiveDateTimeForTable(rawValueForColumn)}
      </span>
    );
  }

  if (isCallExecutiveFedexIdAccessor(accessor)) {
    const stableKey = String(rowRecord?.id ?? rowRecord?.candidateId ?? rowIndex);
    return (
      <textarea
        key={`call-exec-fedex-${stableKey}-${accessor}`}
        className="form-control form-control-sm ces-quick-input ces-quick-textarea"
        rows={2}
        defaultValue={rawValueForColumn != null ? String(rawValueForColumn) : ""}
        onBlur={(event) =>
          onQuickUpdateBlur?.(rowRecord, event.target.value, accessor)
        }
        aria-label="Fedex ID"
      />
    );
  }

  if (isCallExecutiveQuickUpdateAccessor(accessor)) {
    const stableKey = String(rowRecord?.id ?? rowRecord?.candidateId ?? rowIndex);
    return (
      <textarea
        key={`call-exec-quick-${stableKey}-${accessor}`}
        className="form-control form-control-sm ces-quick-input ces-quick-textarea"
        rows={2}
        defaultValue={rawValueForColumn != null ? String(rawValueForColumn) : ""}
        onBlur={(event) =>
          onQuickUpdateBlur?.(rowRecord, event.target.value, accessor)
        }
        aria-label="Call quick update"
      />
    );
  }

  const accessorLower = String(accessor ?? "").toLowerCase();
  if (accessorLower.includes("phone")) {
    const phoneDisplay = resolveCallExecutivePhoneDisplayValue(
      rowRecord,
      getItemValueByAccessor
    );
    return (
      <div className="ces-phone-cell">
        <span className="ces-stack-text ces-phone-display">{phoneDisplay || "—"}</span>
      </div>
    );
  }

  return (
    <span className="ces-stack-text">
      {formatDateTimeValueForTable(rawValueForColumn, accessor)}
    </span>
  );
};



/**
 * Maps metadata `autoComplete.api` identifiers to async picker kinds (same contract as AllCommonFilterModal).
 */
export const METADATA_AUTOCOMPLETE_API_IDENTIFIER_TO_KIND = {
  get_client_options: "client",
  get_candidate_options: "candidate",
  get_system_users_options: "user",
  get_source_options: "source",
};

const findReactSelectOptionByValueOrNull = (availableOptionsList, selectedValueFromFormState) =>
  (Array.isArray(availableOptionsList) ? availableOptionsList : []).find(
    (optionItem) => String(optionItem?.value ?? "") === String(selectedValueFromFormState ?? "")
  ) || null;

/** react-select overlays need a high stacking context above the candidate table/card. */
const reactSelectElevatedOverlayStyles = {
  menuPortal: (baseStyles) => ({ ...baseStyles, zIndex: 9999 }),
  menu: (baseStyles) => ({ ...baseStyles, zIndex: 9999 }),
};

const reactSelectToolbarControlStyles = {
  ...reactSelectElevatedOverlayStyles,
  control: (baseStyles) => ({
    ...baseStyles,
    display: "flex",
    alignItems: "center",
  }),
  valueContainer: (baseStyles) => ({
    ...baseStyles,
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    flexWrap: "nowrap",
    height: "100%",
    paddingTop: 0,
    paddingBottom: 0,
    position: "relative",
  }),
  singleValue: (baseStyles) => ({
    ...baseStyles,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "calc(100% - 16px)",
    position: "absolute",
    top: "50%",
    left: 8,
    transform: "translateY(-50%)",
    marginTop: 0,
    marginBottom: 0,
    lineHeight: 1.25,
  }),
  placeholder: (baseStyles) => ({
    ...baseStyles,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "calc(100% - 16px)",
    position: "absolute",
    top: "50%",
    left: 8,
    transform: "translateY(-50%)",
    marginTop: 0,
    marginBottom: 0,
    lineHeight: 1.25,
  }),
  input: (baseStyles) => ({
    ...baseStyles,
    margin: 0,
    paddingTop: 0,
    paddingBottom: 0,
    lineHeight: 1.25,
  }),
  indicatorsContainer: (baseStyles) => ({
    ...baseStyles,
    height: "100%",
    alignSelf: "stretch",
    alignItems: "center",
  }),
};

/** Toolbar multiselect — same 38px control height as other upper filters; chips scroll horizontally. */
const reactSelectToolbarMultiselectControlStyles = {
  ...reactSelectElevatedOverlayStyles,
  control: (baseStyles) => ({
    ...baseStyles,
    minHeight: 38,
    maxHeight: 38,
    display: "flex",
    alignItems: "center",
  }),
  valueContainer: (baseStyles) => ({
    ...baseStyles,
    display: "flex",
    flexWrap: "nowrap",
    alignItems: "center",
    overflow: "hidden",
    height: "100%",
    paddingTop: 0,
    paddingBottom: 0,
    paddingLeft: 6,
    paddingRight: 4,
    gap: 2,
  }),
  multiValue: (baseStyles) => ({
    ...baseStyles,
    flex: "0 0 auto",
    margin: 0,
  }),
  multiValueLabel: (baseStyles) => ({
    ...baseStyles,
    whiteSpace: "nowrap",
    padding: "1px 4px",
    fontSize: "0.8125rem",
  }),
  multiValueRemove: (baseStyles) => ({
    ...baseStyles,
    paddingLeft: 3,
    paddingRight: 3,
  }),
  input: (baseStyles) => ({
    ...baseStyles,
    margin: 0,
    padding: 0,
    minWidth: "2px",
    flex: "0 0 auto",
  }),
  indicatorsContainer: (baseStyles) => ({
    ...baseStyles,
    height: "100%",
    alignSelf: "stretch",
    alignItems: "center",
  }),
};

 /**
   * Upper-toolbar control factory: mirrors AllCommonFilterModal behaviour for the subset of types we expect
   * in `isDefault: true` metadata (search box, remote dropdowns, async client pickers, etc.).
   */
 export const renderCandidateMetadataToolbarFilterControl = (filterDefinitionFromMetadataApi, toolbarFilterColumnIndex, modifyListFilter, handleToolbarTextFilterInputChange, handleToolbarReactSelectFilterChange, handleUpperToolbarCandidateFilterSearch, toolbarDropdownReactSelectOptionsByFieldName = {}, toolbarColumnMdLayout = null, searchOnEnter = true, pageName = "") => {
  const backendFilterFieldKey = filterDefinitionFromMetadataApi?.field;
  const metadataDeclaredControlType = String(
    filterDefinitionFromMetadataApi?.type || "text"
  ).toLowerCase();
  const currentValueFromReactState = modifyListFilter?.[backendFilterFieldKey] ?? "";
  const placeholderCopyFromMetadata =
    filterDefinitionFromMetadataApi?.placeholder ||
    filterDefinitionFromMetadataApi?.title ||
    "";
  const filterLabelText =
    filterDefinitionFromMetadataApi?.title ||
    placeholderCopyFromMetadata ||
    backendFilterFieldKey ||
    "Filter";

  const isFilterFieldActive = isMetadataFilterValueActive(
    currentValueFromReactState,
    metadataDeclaredControlType
  );
  const metadataFilterFieldClassName = buildUpperFilterFieldColClassName(
    metadataDeclaredControlType,
    backendFilterFieldKey,
    isFilterFieldActive
  );

  const colProps = (fallbackMd = "4", fallbackLg = "3") => {
    if (Array.isArray(toolbarColumnMdLayout) && toolbarColumnMdLayout.length) {
      const md = String(
        toolbarColumnMdLayout[toolbarFilterColumnIndex] ??
          toolbarColumnMdLayout[toolbarColumnMdLayout.length - 1] ??
          2
      );
      return { xs: "12", md, lg: md, className: metadataFilterFieldClassName };
    }
    return {
      xs: "12",
      md: fallbackMd,
      lg: fallbackLg,
      className: metadataFilterFieldClassName,
    };
  };

  const wrapToolbarFilterControl = (controlNode) => (
    <Col
      key={`${filterDefinitionFromMetadataApi.field}-${toolbarFilterColumnIndex}`}
      {...colProps("4", "3")}
    >
      <span className="manage-user-filter-label">{filterLabelText}</span>
      <div className="metadata-filter-field__control">{controlNode}</div>
    </Col>
  );

  if (metadataDeclaredControlType === "text") {
    return (
      <Col key={`${filterDefinitionFromMetadataApi.field}-${toolbarFilterColumnIndex}`} {...colProps("4", "4")}>
      <span className="manage-user-filter-label">{filterLabelText}</span>
      <div className="metadata-filter-field__control">
      <Input
        type="text"
        name={backendFilterFieldKey}
        placeholder={placeholderCopyFromMetadata}
        className="border w-100 category-search-input"
        value={currentValueFromReactState}
        onChange={(domInputChangeEvent) => {
          handleToolbarTextFilterInputChange(
            backendFilterFieldKey,
            domInputChangeEvent?.target?.value || ""
          );
        }}
        onKeyDown={
          searchOnEnter
            ? (keyboardEvent) => {
                if (keyboardEvent.key === "Enter") {
                  handleUpperToolbarCandidateFilterSearch();
                }
              }
            : undefined
        }
      />
      </div>
      </Col>
    );
  }

  if (metadataDeclaredControlType === "dropdown") {
    const reactSelectOptionsForThisField =
      toolbarDropdownReactSelectOptionsByFieldName[backendFilterFieldKey] || [];
    return (
      <Col key={`${filterDefinitionFromMetadataApi.field}-${toolbarFilterColumnIndex}`} {...colProps("4", "3")}>
      <span className="manage-user-filter-label">{filterLabelText}</span>
      <div className="metadata-filter-field__control">
      <Select
        options={reactSelectOptionsForThisField}
        className="select2-selection"
        placeholder={
          placeholderCopyFromMetadata ||
          filterDefinitionFromMetadataApi?.title ||
          "Select"
        }
        isClearable
        value={findReactSelectOptionByValueOrNull(
          reactSelectOptionsForThisField,
          currentValueFromReactState
        )}
        onChange={(selectedOption) =>
          handleToolbarReactSelectFilterChange(backendFilterFieldKey, selectedOption)
        }
        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
        styles={reactSelectToolbarControlStyles}
        menuPosition="fixed"
      />
      </div>
      </Col>
    );
  }

  if (metadataDeclaredControlType === "multiselect") {
    const reactSelectOptionsForThisField =
      toolbarDropdownReactSelectOptionsByFieldName[backendFilterFieldKey] || [];
    const selectedValueList = Array.isArray(currentValueFromReactState)
      ? currentValueFromReactState
      : [];
    const selectedValueSet = new Set(
      selectedValueList.map((value) => String(value ?? ""))
    );
    const selectedMultiselectOptions = reactSelectOptionsForThisField.filter((option) =>
      selectedValueSet.has(String(option?.value ?? ""))
    );
    return (
      <Col key={`${filterDefinitionFromMetadataApi.field}-${toolbarFilterColumnIndex}`} {...colProps("4", "3")}>
      <span className="manage-user-filter-label">{filterLabelText}</span>
      <div className="metadata-filter-field__control">
      <Select
        isMulti
        options={reactSelectOptionsForThisField}
        className="select2-selection"
        classNamePrefix="manage-user-filter-multiselect"
        placeholder={
          placeholderCopyFromMetadata ||
          filterDefinitionFromMetadataApi?.title ||
          "Select"
        }
        closeMenuOnSelect={false}
        value={selectedMultiselectOptions}
        onChange={(selectedOptions) =>
          handleToolbarTextFilterInputChange(
            backendFilterFieldKey,
            (selectedOptions || []).map((option) => option?.value)
          )
        }
        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
        styles={reactSelectToolbarMultiselectControlStyles}
        menuPosition="fixed"
      />
      </div>
      </Col>
    );
  }

  if (metadataDeclaredControlType === "select") {
    const staticItemArrayFromMetadata =
      filterDefinitionFromMetadataApi?.itemArray ??
      filterDefinitionFromMetadataApi?.options ??
      [];
    return (
      <Col key={`${filterDefinitionFromMetadataApi.field}-${toolbarFilterColumnIndex}`} {...colProps("4", "3")}>
      <span className="manage-user-filter-label">{filterLabelText}</span>
      <div className="metadata-filter-field__control">
      <Select
        options={staticItemArrayFromMetadata}
        className="select2-selection"
        placeholder={
          placeholderCopyFromMetadata ||
          filterDefinitionFromMetadataApi?.title ||
          "Select"
        }
        isClearable
        value={findReactSelectOptionByValueOrNull(
          staticItemArrayFromMetadata,
          currentValueFromReactState
        )}
        onChange={(selectedOption) =>
          handleToolbarReactSelectFilterChange(backendFilterFieldKey, selectedOption)
        }
        menuPortalTarget={typeof document !== "undefined" ? document.body : null}
        styles={reactSelectToolbarControlStyles}
        menuPosition="fixed"
      />
      </div>
      </Col>
    );
  }

  if (metadataDeclaredControlType === "date") {
    return (
      <Col key={`${filterDefinitionFromMetadataApi.field}-${toolbarFilterColumnIndex}`} {...colProps("4", "3")}>
      <span className="manage-user-filter-label">{filterLabelText}</span>
      <div className="metadata-filter-field__control">
      <Input
        type="date"
        name={backendFilterFieldKey}
        className="border w-100 category-search-input"
        value={currentValueFromReactState || ""}
        onChange={(domInputChangeEvent) =>
          handleToolbarTextFilterInputChange(
            backendFilterFieldKey,
            domInputChangeEvent?.target?.value || ""
          )
        }
      />
      </div>
      </Col>
    );
  }

  const asyncSelectInitialOption = buildAsyncFilterInitialOption(
    backendFilterFieldKey,
    currentValueFromReactState,
    modifyListFilter
  );

  if (metadataDeclaredControlType === "asyncselect") {
    const asyncResourceKind = filterDefinitionFromMetadataApi?.asyncKind;
    if (asyncResourceKind === "client") {
      return wrapToolbarFilterControl(
        <AsyncClientSelect
          key={`${backendFilterFieldKey}-${String(currentValueFromReactState ?? "")}`}
          value={currentValueFromReactState}
          pageName={pageName}
          initialOption={asyncSelectInitialOption}
          onChange={(selectedOption) =>
            handleToolbarReactSelectFilterChange(backendFilterFieldKey, selectedOption)
          }
          placeholder={placeholderCopyFromMetadata || "Select Client"}
        />
      );
    }
    if (asyncResourceKind === "source") {
      return wrapToolbarFilterControl(
        <AsyncSourceSelect
          key={`${backendFilterFieldKey}-${String(currentValueFromReactState ?? "")}`}
          value={currentValueFromReactState}
          initialOption={asyncSelectInitialOption}
          onChange={(selectedOption) =>
            handleToolbarReactSelectFilterChange(backendFilterFieldKey, selectedOption)
          }
          placeholder={placeholderCopyFromMetadata || "Select Source"}
        />
      );
    }
    if (asyncResourceKind === "user") {
      return wrapToolbarFilterControl(
        <AsyncUserSelect
          key={`${backendFilterFieldKey}-${String(currentValueFromReactState ?? "")}`}
          value={currentValueFromReactState}
          pageName={pageName}
          initialOption={asyncSelectInitialOption}
          onChange={(selectedOption) =>
            handleToolbarReactSelectFilterChange(backendFilterFieldKey, selectedOption)
          }
          placeholder={placeholderCopyFromMetadata || "Select User"}
        />
      );
    }
    if (asyncResourceKind === "candidate") {
      const scopedClientId = String(modifyListFilter?.clientId ?? "").trim();
      return wrapToolbarFilterControl(
        <AsyncCandidateSelect
          key={`${backendFilterFieldKey}-${scopedClientId}-${String(currentValueFromReactState ?? "")}`}
          value={currentValueFromReactState}
          clientId={scopedClientId}
          minInputLength={0}
          emptyHint="Type to search candidates"
          initialOption={asyncSelectInitialOption}
          onChange={(selectedOption) =>
            handleToolbarReactSelectFilterChange(backendFilterFieldKey, selectedOption)
          }
          placeholder="Filter Candidate"
        />
      );
    }
  }

  if (metadataDeclaredControlType === "autocomplete") {
    const asyncResourceKind =
      METADATA_AUTOCOMPLETE_API_IDENTIFIER_TO_KIND[filterDefinitionFromMetadataApi?.api];
    if (asyncResourceKind === "client") {
      return wrapToolbarFilterControl(
        <AsyncClientSelect
          key={`${backendFilterFieldKey}-${String(currentValueFromReactState ?? "")}`}
          value={currentValueFromReactState}
          pageName={pageName}
          initialOption={asyncSelectInitialOption}
          onChange={(selectedOption) =>
            handleToolbarReactSelectFilterChange(backendFilterFieldKey, selectedOption)
          }
          placeholder={placeholderCopyFromMetadata || "Select Client"}
        />
      );
    }
    if (asyncResourceKind === "source") {
      return wrapToolbarFilterControl(
        <AsyncSourceSelect
          key={`${backendFilterFieldKey}-${String(currentValueFromReactState ?? "")}`}
          value={currentValueFromReactState}
          initialOption={asyncSelectInitialOption}
          onChange={(selectedOption) =>
            handleToolbarReactSelectFilterChange(backendFilterFieldKey, selectedOption)
          }
          placeholder={placeholderCopyFromMetadata || "Select Source"}
        />
      );
    }
    if (asyncResourceKind === "user") {
      return wrapToolbarFilterControl(
        <AsyncUserSelect
          key={`${backendFilterFieldKey}-${String(currentValueFromReactState ?? "")}`}
          value={currentValueFromReactState}
          pageName={pageName}
          initialOption={asyncSelectInitialOption}
          onChange={(selectedOption) =>
            handleToolbarReactSelectFilterChange(backendFilterFieldKey, selectedOption)
          }
          placeholder={placeholderCopyFromMetadata || "Select User"}
        />
      );
    }
    if (asyncResourceKind === "candidate") {
      const scopedClientId = String(modifyListFilter?.clientId ?? "").trim();
      return wrapToolbarFilterControl(
        <AsyncCandidateSelect
          key={`${backendFilterFieldKey}-${scopedClientId}-${String(currentValueFromReactState ?? "")}`}
          value={currentValueFromReactState}
          clientId={scopedClientId}
          minInputLength={0}
          emptyHint="Type to search candidates"
          initialOption={asyncSelectInitialOption}
          onChange={(selectedOption) =>
            handleToolbarReactSelectFilterChange(backendFilterFieldKey, selectedOption)
          }
          placeholder="Filter Candidate"
        />
      );
    }
  }

  return (
    <Col key={`${filterDefinitionFromMetadataApi.field}-${toolbarFilterColumnIndex}`} {...colProps("4", "3")}>
    <span className="manage-user-filter-label">{filterLabelText}</span>
    <div className="metadata-filter-field__control">
    <Input
      type="text"
      name={backendFilterFieldKey}
      placeholder={placeholderCopyFromMetadata}
      className="border w-100 category-search-input"
      value={currentValueFromReactState}
      onChange={(domInputChangeEvent) => {
        handleToolbarTextFilterInputChange(
          backendFilterFieldKey,
          domInputChangeEvent?.target?.value || ""
        );
      }}
      onKeyDown={
        searchOnEnter
          ? (keyboardEvent) => {
              if (keyboardEvent.key === "Enter") {
                handleUpperToolbarCandidateFilterSearch();
              }
            }
          : undefined
      }
    />
    </div>
    </Col>
  );
};

/** `type: "hide"` from API metadata → column hidden until enabled in Manage Columns. */
export const isTableColumnHiddenByDefaultFromMetadata = (columnDefinition = {}) =>
  String(columnDefinition?.type ?? "").toLowerCase() === "hide";

/** Stable key for column prefs — prefers API `keyName`, then `accessor`, then `field`. */
export const getTableColumnPreferenceKey = (columnDefinition = {}) => {
  const keyName = String(
    columnDefinition?.keyName ?? columnDefinition?.keyname ?? ""
  ).trim();
  if (keyName) return keyName;
  const accessor = String(columnDefinition?.accessor ?? "").trim();
  if (accessor) return accessor;
  return String(columnDefinition?.field ?? "").trim();
};

/** Whether a column is visible by default in Manage Columns (API `defaultChecked` or not `type: hide`). */
export const isTableColumnVisibleByDefaultFromMetadata = (columnDefinition = {}) => {
  const defaultCheckedRaw =
    columnDefinition?.defaultChecked ?? columnDefinition?.defaultchecked;
  if (defaultCheckedRaw !== undefined && defaultCheckedRaw !== null) {
    return Boolean(defaultCheckedRaw);
  }
  return !isTableColumnHiddenByDefaultFromMetadata(columnDefinition);
};

export const findTableColumnDefinitionByPreferenceKey = (
  columns = [],
  preferenceKey = ""
) => {
  const key = String(preferenceKey ?? "").trim();
  if (!key) return undefined;
  return (Array.isArray(columns) ? columns : []).find(
    (columnDefinition) => getTableColumnPreferenceKey(columnDefinition) === key
  );
};

/** Map API `table.columns[]` row → react-table column (accessor, keyName, defaultChecked, type). */
export const mapApiMetadataToTableColumnDefinition = (col = {}, options = {}) => {
  const field = String(col?.field ?? "").trim();
  const accessorFromOptions =
    typeof options.resolveAccessor === "function"
      ? options.resolveAccessor(col)
      : null;
  const accessor = String(
    accessorFromOptions ?? col?.accessor ?? (field ? pascalToCamel(field) : "")
  ).trim();
  const keyName = String(col?.keyName ?? col?.keyname ?? accessor ?? "").trim();
  const defaultCheckedRaw = col?.defaultChecked ?? col?.defaultchecked;
  const columnDefinition = {
    Header: col?.title || keyName || accessor,
    accessor,
    keyName,
    type: col?.type,
  };
  if (defaultCheckedRaw !== undefined && defaultCheckedRaw !== null) {
    columnDefinition.defaultChecked = Boolean(defaultCheckedRaw);
  }
  return columnDefinition;
};

export const mapApiMetadataTableColumns = (metaColumns = [], options = {}) => {
  if (!Array.isArray(metaColumns) || !metaColumns.length) return [];
  return metaColumns
    .map((col) => mapApiMetadataToTableColumnDefinition(col, options))
    .filter((columnDefinition) => getTableColumnPreferenceKey(columnDefinition));
};

/** Extract `table.columns` (or `columns`) from GET meta/metadata API payloads. */
export const resolveMetadataTableColumnsFromApiResponse = (
  metadataResponse = {},
  options = {}
) => {
  const meta =
    metadataResponse?.data != null && typeof metadataResponse.data === "object"
      ? metadataResponse.data
      : metadataResponse;
  const normalizedMeta = meta && typeof meta === "object" ? meta : {};
  const rawColumns =
    normalizedMeta?.table?.columns ??
    normalizedMeta?.columns ??
    normalizedMeta?.Table?.columns ??
    normalizedMeta?.Table?.Columns ??
    [];
  return mapApiMetadataTableColumns(rawColumns, options);
};

export const normalizeTableColumnDefinitionsForPreferences = (columns = []) =>
  (Array.isArray(columns) ? columns : []).map((columnDefinition) => {
    const preferenceKey = getTableColumnPreferenceKey(columnDefinition);
    return {
      ...columnDefinition,
      keyName: String(
        columnDefinition?.keyName ?? columnDefinition?.keyname ?? preferenceKey
      ).trim(),
      accessor: String(columnDefinition?.accessor ?? preferenceKey).trim() || preferenceKey,
    };
  });

export const buildDefaultTableColumnOrder = (columns = []) =>
  (Array.isArray(columns) ? columns : [])
    .map((columnDefinition) => getTableColumnPreferenceKey(columnDefinition))
    .filter(Boolean);

/** Stable key for metadata column sets — drives preference sync when API columns change. */
export const buildTableColumnsSignature = (columns = []) =>
  buildDefaultTableColumnOrder(columns)
    .map((preferenceKey) => {
      const columnDefinition = findTableColumnDefinitionByPreferenceKey(
        columns,
        preferenceKey
      );
      const typeToken = String(columnDefinition?.type ?? "").toLowerCase();
      const defaultCheckedRaw =
        columnDefinition?.defaultChecked ?? columnDefinition?.defaultchecked;
      const defaultCheckedToken =
        defaultCheckedRaw !== undefined && defaultCheckedRaw !== null
          ? `:dc${defaultCheckedRaw ? 1 : 0}`
          : "";
      return typeToken
        ? `${preferenceKey}:${typeToken}${defaultCheckedToken}`
        : `${preferenceKey}${defaultCheckedToken}`;
    })
    .join("|");

/** Drop stale accessors; append any new metadata columns at the end. */
export const getEffectiveTableColumnOrder = (columnOrder = [], columns = []) => {
  const incomingAccessors = buildDefaultTableColumnOrder(columns);
  if (!incomingAccessors.length) return [];
  return mergeTableColumnOrderWithDefinitions(columnOrder, columns);
};

/** Manage Columns modal + exports — metadata definitions in persisted order. */
export const getOrderedTableColumnDefinitions = (columns = [], columnOrder = []) => {
  const columnByPreferenceKey = new Map(
    (Array.isArray(columns) ? columns : [])
      .map((columnDefinition) => {
        const preferenceKey = getTableColumnPreferenceKey(columnDefinition);
        return preferenceKey ? [preferenceKey, columnDefinition] : null;
      })
      .filter(Boolean)
  );
  const ordered = [];
  const effectiveOrder = getEffectiveTableColumnOrder(columnOrder, columns);
  effectiveOrder.forEach((preferenceKey) => {
    const columnDefinition = columnByPreferenceKey.get(preferenceKey);
    if (columnDefinition) {
      ordered.push(columnDefinition);
      columnByPreferenceKey.delete(preferenceKey);
    }
  });
  columnByPreferenceKey.forEach((columnDefinition) => ordered.push(columnDefinition));
  return ordered;
};

/** Visible headers only — contiguous drag indices for react-beautiful-dnd. */
export const getVisibleTableColumnOrder = (columnOrder = [], visibleColumns = {}) =>
  (Array.isArray(columnOrder) ? columnOrder : []).filter(
    (accessorKey) => visibleColumns?.[accessorKey]
  );

export const reorderVisibleTableColumnOrder = (
  fullColumnOrder = [],
  visibleColumns = {},
  sourceIndex,
  destinationIndex
) => {
  const visibleOrder = getVisibleTableColumnOrder(fullColumnOrder, visibleColumns);
  const reorderedVisible = reorderTableColumnOrder(
    visibleOrder,
    sourceIndex,
    destinationIndex
  );
  if (reorderedVisible === visibleOrder) return fullColumnOrder;

  const visibleIterator = reorderedVisible[Symbol.iterator]();
  return (Array.isArray(fullColumnOrder) ? fullColumnOrder : []).map((accessorKey) => {
    if (!visibleColumns?.[accessorKey]) return accessorKey;
    return visibleIterator.next().value ?? accessorKey;
  });
};

export const buildDefaultTableVisibleColumnsMap = (columns = []) =>
  (Array.isArray(columns) ? columns : []).reduce((accumulator, columnDefinition) => {
    const preferenceKey = getTableColumnPreferenceKey(columnDefinition);
    if (!preferenceKey) return accumulator;
    return {
      ...accumulator,
      [preferenceKey]: isTableColumnVisibleByDefaultFromMetadata(columnDefinition),
    };
  }, {});

export const getTableColumnPreferencesStorageKey = (tableKey = "") => {
  const normalized = String(tableKey ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_-]/g, "");
  return `ats_table_column_prefs_${normalized || "default"}`;
};

export const TABLE_COLUMN_PREFS_SETTINGS_KEY_PREFIX = "ats_table_column_prefs_";

export const parseTableColumnPreferencesSettingsValue = (settingsValue) => {
  let parsed = settingsValue;

  if (typeof settingsValue === "string") {
    try {
      parsed = JSON.parse(settingsValue);
    } catch {
      return null;
    }
  }

  if (!parsed || typeof parsed !== "object") return null;

  return {
    columnOrder: Array.isArray(parsed.columnOrder) ? parsed.columnOrder : [],
    visibleColumns:
      parsed.visibleColumns && typeof parsed.visibleColumns === "object"
        ? parsed.visibleColumns
        : {},
  };
};

/** Write server `settingsValue` into localStorage under `settingsKey`. */
export const applyTableColumnPreferenceUserSettingToStorage = (
  settingsKey = "",
  settingsValue = null
) => {
  const key = String(settingsKey ?? "").trim();
  if (!key.startsWith(TABLE_COLUMN_PREFS_SETTINGS_KEY_PREFIX)) return false;

  const preferences = parseTableColumnPreferencesSettingsValue(settingsValue);
  if (!preferences) return false;

  try {
    localStorage.setItem(key, JSON.stringify(preferences));
    return true;
  } catch {
    return false;
  }
};

export const loadTableColumnPreferencesFromStorage = (tableKey = "") => {
  try {
    const raw = localStorage.getItem(getTableColumnPreferencesStorageKey(tableKey));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return {
      columnOrder: Array.isArray(parsed.columnOrder) ? parsed.columnOrder : [],
      visibleColumns:
        parsed.visibleColumns && typeof parsed.visibleColumns === "object"
          ? parsed.visibleColumns
          : {},
    };
  } catch {
    return null;
  }
};

export const saveTableColumnPreferencesToStorage = (tableKey = "", preferences = {}) => {
  try {
    localStorage.setItem(
      getTableColumnPreferencesStorageKey(tableKey),
      JSON.stringify({
        columnOrder: Array.isArray(preferences.columnOrder) ? preferences.columnOrder : [],
        visibleColumns:
          preferences.visibleColumns && typeof preferences.visibleColumns === "object"
            ? preferences.visibleColumns
            : {},
      })
    );
  } catch {
    /* ignore quota / private mode */
  }
};

export const mergeTableColumnOrderWithDefinitions = (
  previousOrder = [],
  columns = []
) => {
  const incomingAccessors = buildDefaultTableColumnOrder(columns);
  if (!Array.isArray(previousOrder) || previousOrder.length === 0) {
    return incomingAccessors;
  }
  const retainedOrder = previousOrder.filter((accessorKey) =>
    incomingAccessors.includes(accessorKey)
  );
  incomingAccessors.forEach((accessorKey, definitionIndex) => {
    if (retainedOrder.includes(accessorKey)) return;
    let insertAt = retainedOrder.length;
    for (let i = definitionIndex - 1; i >= 0; i -= 1) {
      const precedingKey = incomingAccessors[i];
      const precedingIndex = retainedOrder.indexOf(precedingKey);
      if (precedingIndex !== -1) {
        insertAt = precedingIndex + 1;
        break;
      }
    }
    retainedOrder.splice(insertAt, 0, accessorKey);
  });

  for (let i = 0; i < retainedOrder.length; i += 1) {
    const key = retainedOrder[i];
    const keyDefinitionIndex = incomingAccessors.indexOf(key);
    if (keyDefinitionIndex === -1) continue;
    for (let j = 0; j < i; j += 1) {
      const earlierKey = retainedOrder[j];
      const earlierDefinitionIndex = incomingAccessors.indexOf(earlierKey);
      if (earlierDefinitionIndex === -1) continue;
      if (earlierDefinitionIndex > keyDefinitionIndex) {
        retainedOrder.splice(i, 1);
        retainedOrder.splice(j, 0, key);
        i = j;
        break;
      }
    }
  }

  return retainedOrder;
};

export const mergeTableVisibleColumnsWithDefinitions = (
  previousVisibilityMap = {},
  columns = []
) => {
  const incomingAccessors = buildDefaultTableColumnOrder(columns);
  const nextVisibilityMap = { ...previousVisibilityMap };
  let visibilityChanged = false;

  incomingAccessors.forEach((preferenceKey) => {
    const columnDefinition = findTableColumnDefinitionByPreferenceKey(
      columns,
      preferenceKey
    );
    if (!(preferenceKey in nextVisibilityMap)) {
      nextVisibilityMap[preferenceKey] = columnDefinition
        ? isTableColumnVisibleByDefaultFromMetadata(columnDefinition)
        : true;
      visibilityChanged = true;
    }
  });

  Object.keys(nextVisibilityMap).forEach((storedAccessorKey) => {
    if (!incomingAccessors.includes(storedAccessorKey)) {
      delete nextVisibilityMap[storedAccessorKey];
      visibilityChanged = true;
    }
  });

  return visibilityChanged ? { ...nextVisibilityMap } : { ...previousVisibilityMap };
};

export const reorderTableColumnOrder = (columnOrder = [], sourceIndex, destinationIndex) => {
  if (sourceIndex === destinationIndex) return columnOrder;
  const reordered = Array.from(columnOrder);
  const [removed] = reordered.splice(sourceIndex, 1);
  reordered.splice(destinationIndex, 0, removed);
  return reordered;
};

/** Merge stored / defaults with current column metadata definitions. */
export const resolveTableColumnPreferences = (
  tableKey = "",
  columns = [],
  storedPreferences = null
) => {
  const stored =
    storedPreferences ?? loadTableColumnPreferencesFromStorage(tableKey);
  const basePreferences = stored || {
    columnOrder: buildDefaultTableColumnOrder(columns),
    visibleColumns: buildDefaultTableVisibleColumnsMap(columns),
  };

  return {
    columnOrder: mergeTableColumnOrderWithDefinitions(
      basePreferences.columnOrder,
      columns
    ),
    visibleColumns: mergeTableVisibleColumnsWithDefinitions(
      basePreferences.visibleColumns,
      columns
    ),
  };
};

/** Default rows-per-page for server-paginated tables app-wide. */
export const DEFAULT_PAGE_SIZE = 100;

/** Shared page-size dropdown options for server-paginated tables. */
export const DEFAULT_PAGE_SIZE_OPTIONS = [
  50,
  DEFAULT_PAGE_SIZE,
  200,
  500,
  1000,
  2000,
];

export const getTablePageSizeStorageKey = (tableKey = "") => {
  const normalized = String(tableKey ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_-]/g, "");
  return `ats_table_page_size_${normalized || "default"}`;
};

export const TABLE_PAGE_SIZE_SETTINGS_KEY_PREFIX = "ats_table_page_size_";

export const parseTablePageSizeSettingsValue = (settingsValue) => {
  let parsed = settingsValue;

  if (typeof settingsValue === "string") {
    try {
      parsed = JSON.parse(settingsValue);
    } catch {
      const numericValue = Number(settingsValue);
      return Number.isFinite(numericValue) ? numericValue : null;
    }
  }

  if (typeof parsed === "number" && Number.isFinite(parsed)) {
    return parsed;
  }

  if (parsed && typeof parsed === "object" && Number.isFinite(Number(parsed.pageSize))) {
    return Number(parsed.pageSize);
  }

  return null;
};

/** Write server `settingsValue` into localStorage under `settingsKey`. */
export const applyTablePageSizeUserSettingToStorage = (
  settingsKey = "",
  settingsValue = null
) => {
  const key = String(settingsKey ?? "").trim();
  if (!key.startsWith(TABLE_PAGE_SIZE_SETTINGS_KEY_PREFIX)) return false;

  const pageSize = parseTablePageSizeSettingsValue(settingsValue);
  if (!Number.isFinite(pageSize)) return false;

  try {
    localStorage.setItem(key, JSON.stringify({ pageSize }));
    return true;
  } catch {
    return false;
  }
};

export const loadTablePageSizeFromStorage = (tableKey = "") => {
  try {
    const raw = localStorage.getItem(getTablePageSizeStorageKey(tableKey));
    if (!raw) return null;
    return parseTablePageSizeSettingsValue(raw);
  } catch {
    return null;
  }
};

export const saveTablePageSizeToStorage = (tableKey = "", pageSize) => {
  const normalizedPageSize = Number(pageSize);
  if (!Number.isFinite(normalizedPageSize)) return;

  try {
    localStorage.setItem(
      getTablePageSizeStorageKey(tableKey),
      JSON.stringify({ pageSize: normalizedPageSize })
    );
  } catch {
    /* ignore quota / private mode */
  }
};

export const resolveTablePageSizePreference = (
  tableKey = "",
  defaultPageSize = DEFAULT_PAGE_SIZE
) => {
  const savedPageSize = loadTablePageSizeFromStorage(tableKey);
  return Number.isFinite(savedPageSize) ? savedPageSize : defaultPageSize;
};

/** Must stay in sync with TableContainer pageName → variant mapping. */
export const CALL_EXECUTIVE_TABLE_PAGE_NAMES = new Set([
  "callexecutiveschedule",
  "callexecutivenotonai",
  "callexecutiveinterview",
  "callexecutivetoday",
  "callexecutivewrongnumber",
  "callexecutiveinprogress",
  "callmanagerorderinprogress",
  "callmanagerreadytosend",
  "callmanagerfurtherreview",
  "callexecutiveunderage",
  "callexecutivemycall",
  "callexecutivedeactivatecandidate",
]);

export const CLIENT_STYLE_TABLE_PAGE_NAMES = new Set([
  "company",
  "team",
  "usertype",
  "workshift",
  "warningletter",
  "monthylist",
  "teamrecognitionmonthylist",
  "client",
  "clientsource",
  "pre-questioning",
  "onboarding",
  "offboarding",
  "clientreview",
  "clientappreciation",
  "jobdescription",
  "clientregister",
  "blockfadvrequestor",
  "findcompanyuser",
  "user ip settings",
  "mailtemplate",
]);

export const TEAM_ONLY_TABLE_PAGE_NAMES = new Set([
  "manageuser",
  "companyuser",
  "airecruiterlog",
]);

export const getCommonTableVariant = (pageName = "") => {
  const pageNameNorm = String(pageName || "").toLowerCase();
  if (CALL_EXECUTIVE_TABLE_PAGE_NAMES.has(pageNameNorm)) return "call-executive";
  if (CLIENT_STYLE_TABLE_PAGE_NAMES.has(pageNameNorm)) return "client";
  if (TEAM_ONLY_TABLE_PAGE_NAMES.has(pageNameNorm)) return "team";
  return "common";
};

/** Matches TableContainer `resolvedTableKey` (pageName + optional tableKey override). */
export const resolveCommonTableKey = (pageName = "", tableKeyProp = "") => {
  const override = String(tableKeyProp ?? "").trim();
  if (override) {
    return override;
  }
  const tableVariant = getCommonTableVariant(pageName);
  if (tableVariant === "call-executive") {
    return `${`call-${pageName ?? "table"}`.trim()}` || "call-table";
  }
  if (tableVariant === "client") {
    return `${`client-${pageName ?? "table"}`.trim()}` || "client-table";
  }
  if (tableVariant === "team") {
    return `${`team-${pageName ?? "table"}`.trim()}` || "team-table";
  }
  return String(pageName ?? "common-table").trim() || "common-table";
};

/** Matches CandidateTableContainer `resolvedTableKey`. */
export const resolveCandidateTableKey = (pageName = "", tableKeyProp = "") => {
  const override = String(tableKeyProp ?? "").trim();
  if (override) return override;
  return (
    String(`candidate-${pageName ?? "table"}`).trim() || "candidate-table"
  );
};

export const CALL_DETAILS_TAB_ORDER_SETTINGS_KEY = "ats_call_details_tab_order";
export const CALL_DETAILS_DEFAULT_TAB_ORDER = ["1", "2", "3"];

export const normalizeCallDetailsTabOrder = (order = []) => {
  const allowed = new Set(CALL_DETAILS_DEFAULT_TAB_ORDER);
  const next = (Array.isArray(order) ? order : [])
    .map((id) => String(id ?? "").trim())
    .filter((id) => allowed.has(id));
  CALL_DETAILS_DEFAULT_TAB_ORDER.forEach((id) => {
    if (!next.includes(id)) next.push(id);
  });
  return next;
};

export const parseCallDetailsTabOrderSettingsValue = (settingsValue) => {
  let parsed = settingsValue;

  if (typeof settingsValue === "string") {
    try {
      parsed = JSON.parse(settingsValue);
    } catch {
      return null;
    }
  }

  if (Array.isArray(parsed)) {
    return normalizeCallDetailsTabOrder(parsed);
  }

  if (parsed && typeof parsed === "object" && Array.isArray(parsed.tabOrder)) {
    return normalizeCallDetailsTabOrder(parsed.tabOrder);
  }

  return null;
};

/** Write server `settingsValue` into localStorage under `ats_call_details_tab_order`. */
export const applyCallDetailsTabOrderUserSettingToStorage = (
  settingsKey = "",
  settingsValue = null
) => {
  const key = String(settingsKey ?? "").trim();
  if (key !== CALL_DETAILS_TAB_ORDER_SETTINGS_KEY) return false;

  const tabOrder = parseCallDetailsTabOrderSettingsValue(settingsValue);
  if (!tabOrder) return false;

  try {
    localStorage.setItem(key, JSON.stringify({ tabOrder }));
    return true;
  } catch {
    return false;
  }
};

export const loadCallDetailsTabOrderFromStorage = () => {
  try {
    const raw = localStorage.getItem(CALL_DETAILS_TAB_ORDER_SETTINGS_KEY);
    if (!raw) return [...CALL_DETAILS_DEFAULT_TAB_ORDER];
    return (
      parseCallDetailsTabOrderSettingsValue(raw) || [...CALL_DETAILS_DEFAULT_TAB_ORDER]
    );
  } catch {
    return [...CALL_DETAILS_DEFAULT_TAB_ORDER];
  }
};

export const saveCallDetailsTabOrderToStorage = (tabOrder = []) => {
  const normalized = normalizeCallDetailsTabOrder(tabOrder);
  try {
    localStorage.setItem(
      CALL_DETAILS_TAB_ORDER_SETTINGS_KEY,
      JSON.stringify({ tabOrder: normalized })
    );
  } catch {
    /* ignore quota / private mode */
  }
  return normalized;
};


